import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { useEncryption } from '../context/EncryptionContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { ArrowLeft, Send, Paperclip, Phone, Video, MoreVertical, Lock, Clock, CheckSquare, Image, FileText, X, Loader2, Check, CheckCheck, Plus, Smile, Trash2, Users, Info, Shield, ChevronDown, LogOut, UserPlus, Search, ChevronUp, Mic, Square, Star, Forward, Copy, Archive, Camera, Bell, BellOff, Ban, ChevronRight, Pin, User, AlertCircle, RotateCcw, Crop } from 'lucide-react';
import { resolveAvatarUrl } from '../lib/utils';
import SetReminderModal from '../components/SetReminderModal';
import CreateTaskModal from '../components/CreateTaskModal';
import TaskMessage from '../components/TaskMessage';
import TaskSummaryMessage from '../components/TaskSummaryMessage';
import LinkPreview from '../components/LinkPreview';
import GroupInfoSidebar from '../components/GroupInfoSidebar';
import SkyHeader from '../components/SkyHeader';
import ExpandableText from '../components/ExpandableText';
import ImageEditor from '../components/ImageEditor';
import ScheduleMessageModal from '../components/ScheduleMessageModal';
import { format } from 'date-fns';
import { toast } from 'sonner';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Resolve media URL - converts relative paths to absolute using current client's backend URL
// Also handles legacy absolute URLs from old preview environments
const resolveMediaUrl = (url) => {
  if (!url) return url;
  // If it's a blob URL, return as-is
  if (url.startsWith('blob:')) return url;
  // If it's an absolute URL pointing to an old preview env, extract the relative path
  if (url.startsWith('http')) {
    const match = url.match(/\/api\/uploads\/.+/);
    if (match) {
      return `${BACKEND_URL}${match[0]}`;
    }
    return url;
  }
  return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Inline audio player with play/pause, progress, and duration
const AudioPlayer = ({ src, messageId, transcript, isDarkMode, token, onTranscribe, isUploading }) => {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [transcribing, setTranscribing] = React.useState(false);

  const formatTime = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const togglePlay = (e) => {
    e.stopPropagation();
    if (isUploading) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      audio.pause();
      setPlaying(false);
    }
  };

  const handleTranscribe = async (e) => {
    if (transcribing || isUploading) return;
    setTranscribing(true);
    try {
      await onTranscribe(e);
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl min-w-[240px] ${isDarkMode ? 'bg-gray-700/50' : 'bg-black/5'}`} data-testid="audio-player">
      <button onClick={togglePlay} className={`w-10 h-10 rounded-full flex items-center justify-center text-white hover:scale-105 transition-transform flex-shrink-0 ${isUploading ? 'bg-gray-500' : 'bg-[#D4AF37]'}`} data-testid="audio-play-btn">
        {isUploading ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : playing ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
        )}
      </button>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => {
          setCurrentTime(e.target.currentTime);
          setProgress(e.target.duration ? (e.target.currentTime / e.target.duration) * 100 : 0);
        }}
        onEnded={() => { setPlaying(false); setProgress(0); setCurrentTime(0); audioRef.current.currentTime = 0; }}
      />
      <div className="flex-1 min-w-0">
        {isUploading && (
          <p className="text-[10px] text-[#D4AF37] mb-1 animate-pulse">Uploading voice note...</p>
        )}
        <div
          className={`h-1.5 rounded-full cursor-pointer ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            const pct = (e.clientX - rect.left) / rect.width;
            if (audioRef.current) { audioRef.current.currentTime = pct * audioRef.current.duration; }
          }}
        >
          <div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {playing || currentTime > 0 ? formatTime(currentTime) : formatTime(duration)}
          </span>
          {duration > 0 && (
            <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatTime(duration)}</span>
          )}
        </div>
        {transcript ? (
          <p className={`text-xs mt-1.5 leading-relaxed font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`} data-testid="voice-transcript">{transcript}</p>
        ) : !isUploading && (
          <button
            onClick={handleTranscribe}
            disabled={transcribing}
            className={`text-[10px] text-[#D4AF37] mt-1 hover:underline font-medium ${transcribing ? 'opacity-50' : ''}`}
            data-testid="transcribe-btn"
          >
            {transcribing ? 'Transcribing...' : 'Transcribe'}
          </button>
        )}
      </div>
    </div>
  );
};
// Download button with circular progress indicator
const DownloadButton = ({ url, filename, className = '' }) => {
  const [downloading, setDownloading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [done, setDone] = React.useState(false);

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    // Allow re-download if already done
    if (done) { setDone(false); }
    setDownloading(true);
    setProgress(0);
    
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    
    xhr.onprogress = (event) => {
      if (event.lengthComputable) {
        setProgress(Math.round((event.loaded / event.total) * 100));
      } else {
        setProgress(prev => Math.min(prev + 5, 90));
      }
    };
    
    xhr.onload = () => {
      if (xhr.status === 200) {
        setProgress(100);
        const blob = xhr.response;
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        setDone(true);
      } else {
        window.open(url, '_blank');
      }
      setDownloading(false);
    };
    
    xhr.onerror = () => {
      window.open(url, '_blank');
      setDownloading(false);
    };
    
    xhr.send();
  };

  const radius = 10;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <button onClick={handleDownload} className={`w-7 h-7 rounded-full flex items-center justify-center ${done ? 'bg-green-500/70' : 'bg-black/40 hover:bg-black/60'} transition-all ${className}`} data-testid="download-media-btn">
      {downloading ? (
        <svg width="24" height="24" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r={radius} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
          <circle cx="12" cy="12" r={radius} fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 12 12)" className="transition-all duration-200" />
        </svg>
      ) : done ? (
        <Check size={12} className="text-white" />
      ) : (
        <ArrowLeft size={10} className="text-white rotate-[-90deg]" />
      )}
    </button>
  );
};

const MessageStatus = ({ message, recipientId, chatType, participantCount, showBlueTicksEnabled = true }) => {
  if (chatType === 'group') {
    const deliveredSet = new Set(message.delivered_to || []);
    const readSet = new Set(message.read_by || []);
    // Remove sender from counts
    deliveredSet.delete(message.sender_id);
    readSet.delete(message.sender_id);
    const totalOthers = Math.max((participantCount || 2) - 1, 1);
    
    // WhatsApp behavior: blue ONLY when ALL members have read, and only if privacy allows
    if (showBlueTicksEnabled && readSet.size >= totalOthers) {
      return <CheckCheck size={14} className="text-blue-500" />;
    } else if (deliveredSet.size > 0) {
      return <CheckCheck size={14} className="text-[#5A4D2C]" />;
    }
    return <Check size={14} className="text-[#5A4D2C]" />;
  }
  
  // Direct chat
  const isDelivered = message.delivered_to?.includes(recipientId);
  const isRead = message.read_by?.includes(recipientId);
  
  if (showBlueTicksEnabled && isRead) {
    return <CheckCheck size={14} className="text-blue-500" />;
  } else if (isDelivered) {
    return <CheckCheck size={14} className="text-[#5A4D2C]" />;
  } else {
    return <Check size={14} className="text-[#5A4D2C]" />;
  }
};

const ChatInterface = ({ isEmbedded = false, onChatUpdated = () => {} }) => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const { typingUsers, sendTyping, sendStopTyping, addMessageHandler, connected, setCurrentChatId } = useWebSocket();
  const { encryptionEnabled, encrypt, decrypt } = useEncryption();
  const { isDarkMode } = useTheme();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [decryptedMessages, setDecryptedMessages] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewCaption, setPreviewCaption] = useState('');
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [failedMessages, setFailedMessages] = useState(new Set());
  const [showTaskSuggestion, setShowTaskSuggestion] = useState(false);
  const [taskSuggestionMessage, setTaskSuggestionMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMessageMenu, setShowMessageMenu] = useState(null); // message id
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [showMessageInfo, setShowMessageInfo] = useState(null); // message info data
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectAllTasks, setSelectAllTasks] = useState(false);
  const [bulkCompleting, setBulkCompleting] = useState(false);
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [chatMenuPos, setChatMenuPos] = useState(null);
  const [clearingChat, setClearingChat] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [replyToMessage, setReplyToMessage] = useState(null); // { id, content, senderName }
  const [showReactionsModal, setShowReactionsModal] = useState(null); // message id for reactions modal
  const [reactionsData, setReactionsData] = useState(null); // detailed reactions with user info
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [searchingMembers, setSearchingMembers] = useState(false);
  // In-chat search
  const [showInChatSearch, setShowInChatSearch] = useState(false);
  const [inChatSearchQuery, setInChatSearchQuery] = useState('');
  const [inChatSearchResults, setInChatSearchResults] = useState([]);
  const [searchingInChat, setSearchingInChat] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  // Voice message
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  // Message forwarding
  const [showForwardModal, setShowForwardModal] = useState(null); // message to forward
  const [forwardChats, setForwardChats] = useState([]);
  // Starred messages
  const [starredMessages, setStarredMessages] = useState([]);
  // Contact profile sidebar
  const [viewingContactProfile, setViewingContactProfile] = useState(null); // { userId, chatId }
  const [contactProfileData, setContactProfileData] = useState(null);
  const [contactCommonGroups, setContactCommonGroups] = useState([]);
  const [contactSharedMedia, setContactSharedMedia] = useState({ images: [], videos: [], documents: [], links: [] });
  const [contactIsMuted, setContactIsMuted] = useState(false);
  const [contactIsBlocked, setContactIsBlocked] = useState(false);
  const [contactMediaTab, setContactMediaTab] = useState('media');
  // Portal-based menu positions
  const [emojiPickerPos, setEmojiPickerPos] = useState(null);
  const [messageMenuPos, setMessageMenuPos] = useState(null);
  // Media lightbox
  const [lightboxMedia, setLightboxMedia] = useState(null); // { url, type }
  // Group media loaded state
  const [groupMediaLoaded, setGroupMediaLoaded] = useState(false);
  const [groupSharedMedia, setGroupSharedMedia] = useState({ images: [], videos: [], documents: [], links: [] });
  // Chat wallpaper
  const [chatWallpaper, setChatWallpaper] = useState('default');
  // Image editor
  const [showImageEditor, setShowImageEditor] = useState(false);
  // Schedule message modal
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  // Inline message editing
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  // Privacy: read receipts
  const [myReadReceiptsEnabled, setMyReadReceiptsEnabled] = useState(true);
  // Profile pic lightbox
  const [expandedAvatar, setExpandedAvatar] = useState(null);
  // Replies viewer popup
  const [viewRepliesFor, setViewRepliesFor] = useState(null); // { id, content, senderName }
  // Pinned messages
  const [pinnedMessages, setPinnedMessages] = useState([]); // { id, content, sender, senderName, timestamp }
  // Helper to close all dropdown menus
  const closeAllMenus = useCallback(() => {
    setShowAttachMenu(false);
    setShowEmojiPicker(false);
    setShowChatMenu(false);
    setShowMessageMenu(null);
    setShowMentionList(false);
  }, []);

  // Quick reaction emojis
  const quickReactions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

  // Handle adding/removing reaction
  const handleReaction = async (messageId, emoji) => {
    try {
      const message = messages.find(m => m.id === messageId);
      const hasReacted = message?.reactions?.[emoji]?.includes(user.id);
      
      if (hasReacted) {
        await axios.delete(`${API}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`, null, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Update local state
      setMessages(prev => prev.map(msg => {
        if (msg.id !== messageId) return msg;
        
        const reactions = { ...(msg.reactions || {}) };
        if (hasReacted) {
          reactions[emoji] = (reactions[emoji] || []).filter(id => id !== user.id);
          if (reactions[emoji].length === 0) delete reactions[emoji];
        } else {
          reactions[emoji] = [...(reactions[emoji] || []), user.id];
        }
        
        return { ...msg, reactions };
      }));
      
      setShowMessageMenu(null);
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  // Fetch detailed reactions with user info
  const fetchReactionsDetails = async (messageId) => {
    try {
      const response = await axios.get(`${API}/messages/${messageId}/reactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReactionsData(response.data);
      setShowReactionsModal(messageId);
    } catch (error) {
      console.error('Failed to fetch reactions:', error);
      toast.error('Failed to load reactions');
    }
  };

  // Escape key handler to close all menus/modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showInChatSearch) {
          setShowInChatSearch(false);
          setInChatSearchQuery('');
          setInChatSearchResults([]);
        } else if (showReactionsModal) {
          setShowReactionsModal(null);
          setReactionsData(null);
        } else if (selectMode) {
          setSelectMode(false);
          setSelectedMessages([]);
        } else if (showAddMemberModal) {
          setShowAddMemberModal(false);
          setMemberSearch('');
          setMemberSearchResults([]);
        } else if (viewingContactProfile) {
          setViewingContactProfile(null);
        } else {
          closeAllMenus();
          setShowGroupInfo(false);
          setShowMessageInfo(null);
        }
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectMode, showReactionsModal, closeAllMenus]);

  // Check if current user is admin (check both chat.admins and groupMembers)
  const isCurrentUserAdmin = chat?.admins?.includes(user.id) || 
    groupMembers.find(m => m.id === user.id)?.is_admin || 
    chat?.created_by === user.id;

  // Search for users to add as members
  const searchForMembers = async (query) => {
    if (!query.trim()) {
      setMemberSearchResults([]);
      return;
    }
    
    setSearchingMembers(true);
    try {
      const response = await axios.get(`${API}/users/search`, {
        params: { query },
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out existing members
      const existingMemberIds = groupMembers.map(m => m.id);
      const filteredResults = response.data.filter(u => !existingMemberIds.includes(u.id));
      setMemberSearchResults(filteredResults);
    } catch (error) {
      console.error('Failed to search users:', error);
    }
    setSearchingMembers(false);
  };

  // Add member to group
  const addMemberToGroup = async (userId) => {
    try {
      await axios.post(`${API}/chats/${chatId}/members/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member added');
      setMemberSearch('');
      setMemberSearchResults([]);
      setShowAddMemberModal(false);
      // Refresh group members
      fetchGroupMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  // Make member admin
  const makeAdmin = async (userId) => {
    try {
      await axios.post(`${API}/chats/${chatId}/admins/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member is now an admin');
      fetchGroupMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make admin');
    }
  };

  // Remove admin status
  const removeAdmin = async (userId) => {
    try {
      await axios.delete(`${API}/chats/${chatId}/admins/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin status removed');
      fetchGroupMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  // Remove member from group
  const removeMember = async (userId) => {
    if (!window.confirm('Remove this member from the group?')) return;
    try {
      await axios.delete(`${API}/chats/${chatId}/members/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member removed');
      fetchGroupMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  // Admin delete message for everyone
  const adminDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message for everyone?')) return;
    try {
      await axios.delete(`${API}/chats/${chatId}/messages/${messageId}/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message deleted for everyone');
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  // In-chat search function
  const searchInChat = async (query) => {
    if (!query.trim()) {
      setInChatSearchResults([]);
      return;
    }
    
    setSearchingInChat(true);
    try {
      const response = await axios.get(`${API}/chats/${chatId}/search`, {
        params: { query },
        headers: { Authorization: `Bearer ${token}` }
      });
      setInChatSearchResults(response.data);
    } catch (error) {
      console.error('Failed to search in chat:', error);
    }
    setSearchingInChat(false);
  };

  // Navigate to search result message
  const navigateToMessage = (messageId) => {
    setHighlightedMessageId(messageId);
    // Find the message element and scroll to it
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Remove highlight after 3 seconds
      setTimeout(() => setHighlightedMessageId(null), 3000);
    }
  };

  // Fetch group members function
  const fetchGroupMembers = async () => {
    if (!chatId || !isGroupChat) return;
    try {
      const response = await axios.get(`${API}/chats/${chatId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch group members:', error);
    }
  };

  // Toggle message selection in multi-select mode
  const toggleMessageSelect = (messageId) => {
    setSelectedMessages(prev => 
      prev.includes(messageId) 
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  // Select all messages
  const selectAllMessages = () => {
    const allIds = messages.filter(m => m.sender_id !== 'system').map(m => m.id);
    setSelectedMessages(allIds);
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        await sendVoiceMessage(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingDuration(0);
      
      // Start duration timer
      const interval = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      recorder.interval = interval;
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      clearInterval(mediaRecorder.interval);
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorder && isRecording) {
      clearInterval(mediaRecorder.interval);
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setMediaRecorder(null);
      setRecordingDuration(0);
    }
  };

  const sendVoiceMessage = async (audioBlob) => {
    // Create optimistic message immediately so user sees it in chat
    const tempId = 'voice_uploading_' + Date.now();
    const tempBlobUrl = URL.createObjectURL(audioBlob);
    const optimisticMsg = {
      id: tempId,
      chat_id: chatId,
      sender_id: user.id,
      content: 'Voice message',
      message_type: 'voice',
      media_url: tempBlobUrl,
      timestamp: new Date().toISOString(),
      read_by: [user.id],
      delivered_to: [user.id],
      _uploading: true
    };
    setMessages(prev => [...prev, optimisticMsg]);
    scrollToBottom();
    
    try {
      setSending(true);
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice_message.webm');
      
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Send message with audio URL
      const response = await axios.post(`${API}/messages`, {
        chat_id: chatId,
        content: 'Voice message',
        message_type: 'voice',
        media_url: uploadResponse.data.url
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const sentVoice = response.data;
      // Replace optimistic message with real one, preserving blob URL for instant playback
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempId);
        if (sentVoice?.id && !filtered.some(m => m.id === sentVoice.id)) {
          // Keep blob URL for immediate playback - audio tag will use it until server URL loads
          const enrichedVoice = { ...sentVoice, _localBlobUrl: tempBlobUrl };
          return [...filtered, enrichedVoice];
        }
        return filtered;
      });
      // Don't revoke blob URL immediately - let AudioPlayer use it
      setTimeout(() => URL.revokeObjectURL(tempBlobUrl), 30000);
    } catch (error) {
      console.error('Failed to send voice message:', error);
      // Mark optimistic message as failed
      setMessages(prev => prev.map(m => m.id === tempId ? { ...m, _uploading: false, status: 'failed', _retryBlob: audioBlob } : m));
    } finally {
      setSending(false);
    }
  };

  // Format recording duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Star/unstar message
  const toggleStarMessage = async (messageId) => {
    try {
      const isStarred = starredMessages.includes(messageId);
      if (isStarred) {
        await axios.delete(`${API}/messages/${messageId}/star`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStarredMessages(prev => prev.filter(id => id !== messageId));
        toast.success('Message unstarred');
      } else {
        await axios.post(`${API}/messages/${messageId}/star`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStarredMessages(prev => [...prev, messageId]);
        toast.success('Message starred');
      }
    } catch (error) {
      toast.error('Failed to update starred status');
    }
    setShowMessageMenu(null);
  };

  // Forward message
  const openForwardModal = async (message) => {
    setShowForwardModal(message);
    // Fetch user's chats for forwarding
    try {
      const response = await axios.get(`${API}/chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForwardChats(response.data);
    } catch (error) {
      console.error('Failed to fetch chats:', error);
    }
  };

  const forwardMessage = async (targetChatId) => {
    if (!showForwardModal) return;
    try {
      await axios.post(`${API}/messages/forward`, {
        message_id: showForwardModal.id,
        target_chat_id: targetChatId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message forwarded');
      setShowForwardModal(null);
    } catch (error) {
      toast.error('Failed to forward message');
    }
  };

  // Copy message to clipboard
  const copyMessage = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
    setShowMessageMenu(null);
  };

  // Mark all messages as read
  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/messages/bulk-read?chat_id=${chatId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All messages marked as read');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to mark messages as read');
    }
  };

  // Bulk delete selected messages
  const handleBulkDelete = async (forEveryone = false) => {
    if (selectedMessages.length === 0) return;
    
    try {
      await axios.post(`${API}/messages/bulk-delete?for_everyone=${forEveryone}`, selectedMessages, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${selectedMessages.length} message(s) deleted`);
      setSelectedMessages([]);
      setSelectMode(false);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete messages');
    }
  };

  // Leave group
  const handleLeaveGroup = async () => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;
    
    try {
      await axios.post(`${API}/chats/${chatId}/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Left the group');
      navigate('/chats');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave group');
    }
  };

  // Delete group (creator only)
  const handleDeleteGroup = async () => {
    if (!window.confirm('Are you sure you want to delete this group? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/chats/${chatId}/group`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Group deleted');
      navigate('/chats');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete group');
    }
  };

  // Touch/swipe handlers for mobile - Direct DOM manipulation for smooth performance
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const touchTargetMsgId = useRef(null);
  const isScrolling = useRef(false);
  const swipeXRef = useRef(0);
  const swipeThreshold = 40; // Lower for easier triggering on received messages

  const handleTouchStart = useCallback((e, messageId) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    touchTargetMsgId.current = messageId;
    isScrolling.current = false;
    swipeXRef.current = 0;
    // Remove transition during drag for instant response
    const el = e.currentTarget;
    if (el) el.style.transition = 'none';
  }, []);

  const handleTouchMove = useCallback((e, messageId) => {
    const diffX = e.touches[0].clientX - touchStartX.current;
    const diffY = Math.abs(e.touches[0].clientY - touchStartY.current);
    
    if (diffY > 15) {
      isScrolling.current = true;
      handleLongPressEnd();
      // Reset position
      const el = e.currentTarget;
      if (el) {
        el.style.transition = 'transform 0.2s ease';
        el.style.transform = '';
      }
      return;
    }
    
    if (Math.abs(diffX) > 10) {
      handleLongPressEnd();
    }
    
    if (!isScrolling.current && diffX > 0) {
      const x = Math.min(diffX * 0.8, 80); // Slight damping for natural feel
      swipeXRef.current = x;
      const el = e.currentTarget;
      if (el) {
        el.style.transform = `translateX(${x}px)`;
      }
      // Show/hide reply icon via data attribute
      const icon = el?.querySelector('[data-swipe-icon]');
      if (icon) {
        const progress = Math.min(x / swipeThreshold, 1);
        icon.style.opacity = String(progress);
        icon.style.transform = `translateY(-50%) scale(${0.5 + progress * 0.5})`;
      }
    }
  }, []);

  const handleTouchEnd = useCallback((e, messageId) => {
    const el = e.currentTarget;
    const swiped = swipeXRef.current >= swipeThreshold;
    
    // Spring back with smooth transition
    if (el) {
      el.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      el.style.transform = '';
    }
    const icon = el?.querySelector('[data-swipe-icon]');
    if (icon) {
      icon.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
      icon.style.opacity = '0';
      icon.style.transform = 'translateY(-50%) scale(0.5)';
    }
    
    if (swiped) {
      // Haptic feedback on swipe
      if (navigator.vibrate) navigator.vibrate(15);
      const msg = messages.find(m => m.id === messageId);
      if (msg) {
        setReplyToMessage({
          id: msg.id,
          content: msg.content?.slice(0, 80),
          senderName: msg.sender_id === user.id ? 'You' : (msg.sender?.display_name || 'User')
        });
        textareaRef.current?.focus();
        // Haptic feedback on native
        if (navigator.vibrate) navigator.vibrate(10);
        // Visual feedback - brief glow effect
        if (el) {
          el.style.boxShadow = '0 0 16px rgba(212, 175, 55, 0.5)';
          el.style.backgroundColor = 'rgba(212, 175, 55, 0.1)';
          el.style.borderRadius = '12px';
          setTimeout(() => {
            el.style.transition = 'box-shadow 0.4s ease, background-color 0.4s ease';
            el.style.boxShadow = '';
            el.style.backgroundColor = '';
          }, 400);
        }
      }
    }
    
    swipeXRef.current = 0;
    touchTargetMsgId.current = null;
  }, [messages, user]);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const textareaRef = useRef(null);

  // Check if this is a system chat (Tasks or Reminders)
  const isSystemChat = chat?.chat_type === 'system';
  const isTasksChat = chat?.system_chat_type === 'tasks';
  const isRemindersChat = chat?.system_chat_type === 'reminders';
  const isGroupChat = chat?.chat_type === 'group';
  const isAdmin = chat?.admins?.includes(user?.id);

  // Long press handlers for mobile - opens message menu with position
  const handleLongPressStart = useCallback((messageId, e) => {
    const touch = e?.touches?.[0];
    if (e?.preventDefault) e.preventDefault();
    longPressTimerRef.current = setTimeout(() => {
      // Set menu position based on touch location
      if (touch) {
        const menuWidth = 200;
        const menuHeight = 400;
        const spaceBelow = window.innerHeight - touch.clientY;
        setMessageMenuPos({
          top: spaceBelow < menuHeight ? Math.max(8, touch.clientY - menuHeight) : touch.clientY + 10,
          left: Math.min(Math.max(8, touch.clientX - menuWidth / 2), window.innerWidth - menuWidth - 8)
        });
      }
      setShowMessageMenu(messageId);
      // Vibrate on long press (mobile feedback)
      if (navigator.vibrate) navigator.vibrate(50);
    }, 500);
  }, []);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  // Task trigger keywords
  const taskKeywords = [
    'can you', 'could you', 'please do', 'please get', 'get this done',
    'need you to', 'want you to', 'would you', 'finish this', 'complete this',
    'todo:', 'task:', 'reminder:', 'don\'t forget', 'remember to'
  ];

  // Check if message contains task keywords
  const checkForTaskKeywords = (text) => {
    const lowerText = text.toLowerCase();
    return taskKeywords.some(keyword => lowerText.includes(keyword));
  };

  // Auto-resize textarea based on content
  const autoResizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    // Reset height to auto to get correct scrollHeight
    textarea.style.height = 'auto';
    
    // Calculate max height based on screen size
    // WhatsApp-style: Web shows ~6 lines (150px), Mobile shows ~4 lines (100px)
    const isMobile = window.innerWidth < 768;
    const maxHeight = isMobile ? 80 : 120;
    const minHeight = 24;
    
    // Set new height (constrained between min and max)
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
  }, []);

  // Handle message input change with task detection
  const handleMessageChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    handleTyping();
    
    // Auto-resize textarea
    setTimeout(autoResizeTextarea, 0);
    
    // Check for @ mention
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1 && chat?.chat_type === 'group') {
      const searchText = value.slice(lastAtIndex + 1);
      if (!searchText.includes(' ')) {
        setMentionSearch(searchText.toLowerCase());
        setShowMentionList(true);
      } else {
        setShowMentionList(false);
      }
    } else {
      setShowMentionList(false);
    }
    
    // Check for task keywords
    if (!isSystemChat && value.length > 10 && checkForTaskKeywords(value)) {
      setShowTaskSuggestion(true);
      setTaskSuggestionMessage(value);
    } else {
      setShowTaskSuggestion(false);
    }
  };

  useEffect(() => {
    initialScrollDone.current = false;
    prevMessageCount.current = 0;
    fetchChat();
    fetchMessages();
    // Fetch privacy settings for read receipt display
    axios.get(`${API}/users/privacy-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setMyReadReceiptsEnabled(res.data.show_read_receipts !== false))
      .catch(() => {});
    // Tell WS context which chat we're viewing (suppress notifications for this chat)
    if (setCurrentChatId) setCurrentChatId(chatId);
    return () => { if (setCurrentChatId) setCurrentChatId(null); };
  }, [chatId]);

  // Poll message status updates (delivery/read receipts) as fallback for cross-container WS
  useEffect(() => {
    const pollStatus = async () => {
      try {
        const res = await axios.get(`${API}/chats/${chatId}/messages?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const fresh = res.data || [];
        setMessages(prev => {
          let changed = false;
          const updated = prev.map(msg => {
            const freshMsg = fresh.find(f => f.id === msg.id);
            if (freshMsg && (
              JSON.stringify(freshMsg.read_by) !== JSON.stringify(msg.read_by) ||
              JSON.stringify(freshMsg.delivered_to) !== JSON.stringify(msg.delivered_to)
            )) {
              changed = true;
              return { ...msg, read_by: freshMsg.read_by, delivered_to: freshMsg.delivered_to };
            }
            return msg;
          });
          return changed ? updated : prev;
        });
      } catch {}
    };
    const interval = setInterval(pollStatus, 8000);
    return () => clearInterval(interval);
  }, [chatId, token]);

  // Listen for message status updates
  useEffect(() => {
    if (!addMessageHandler) return;
    
    const handleStatusUpdate = (data) => {
      if (data.type === 'message_delivered' || data.type === 'message_read') {
        setMessages(prev => prev.map(msg => {
          if (msg.id === data.message_id) {
            if (data.type === 'message_delivered') {
              return { ...msg, delivered_to: [...(msg.delivered_to || []), data.delivered_to] };
            } else {
              return { ...msg, read_by: [...(msg.read_by || []), data.read_by] };
            }
          }
          return msg;
        }));
      } else if (data.type === 'messages_delivered') {
        // Bulk delivery update
        setMessages(prev => prev.map(msg => {
          if (data.message_ids?.includes(msg.id)) {
            return { ...msg, delivered_to: [...(msg.delivered_to || []), data.delivered_to] };
          }
          return msg;
        }));
      } else if (data.type === 'messages_read') {
        // Update locally for immediate feedback
        setMessages(prev => prev.map(msg => {
          if (data.message_ids?.includes(msg.id)) {
            return { ...msg, read_by: [...(msg.read_by || []), data.read_by] };
          }
          return msg;
        }));
      } else if (data.type === 'reaction_update') {
        if (data.chat_id === chatId) {
          setMessages(prev => prev.map(msg => {
            if (msg.id === data.message_id) {
              return { ...msg, reactions: data.reactions };
            }
            return msg;
          }));
        }
      }
    };
    
    const removeHandler = addMessageHandler(handleStatusUpdate);
    return () => removeHandler();
  }, [addMessageHandler, chatId]);

  // Handle incoming WebSocket messages for this chat
  useEffect(() => {
    if (!addMessageHandler) return;
    
    const handleNewMessage = (data) => {
      if (data.type === 'new_message') {
        const msg = data.message || data;
        if (msg.chat_id === chatId) {
          setMessages(prev => {
            if (prev.some(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          // Mark as delivered + read OUTSIDE the state setter (avoids race conditions)
          if (msg.sender_id !== user.id) {
            axios.put(`${API}/messages/${msg.id}/delivered`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => {});
            // Auto-mark as read if chat is visible
            if (document.visibilityState === 'visible') {
              markMessagesAsRead();
            }
          }
        }
      }
    };
    
    const removeHandler = addMessageHandler(handleNewMessage);
    return () => removeHandler();
  }, [addMessageHandler, chatId, user?.id]);

  const markMessageDelivered = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/delivered`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state to show delivered status immediately
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, delivered_to: [...(msg.delivered_to || []), user.id] }
          : msg
      ));
    } catch (error) {
      console.error('Failed to mark message as delivered:', error);
    }
  };

  const scrollTimers = useRef([]);
  const skipAutoScroll = useRef(false);
  const pendingScrollToUnread = useRef(false);

  const cancelPendingScrolls = () => {
    scrollTimers.current.forEach(clearTimeout);
    scrollTimers.current = [];
  };

  const scrollToBottom = () => {
    if (skipAutoScroll.current) return;
    cancelPendingScrolls();
    const doScroll = () => {
      if (messagesEndRef.current && !skipAutoScroll.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
      }
    };
    requestAnimationFrame(doScroll);
    scrollTimers.current.push(setTimeout(doScroll, 150));
    scrollTimers.current.push(setTimeout(doScroll, 400));
  };

  const [unreadBannerCount, setUnreadBannerCount] = useState(0);
  const [firstUnreadId, setFirstUnreadId] = useState(null);
  const prevMessageCount = useRef(0);
  const initialScrollDone = useRef(false);
  const userSentMessage = useRef(false);

  // Reset scroll state when switching chats
  useEffect(() => {
    initialScrollDone.current = false;
    prevMessageCount.current = 0;
    skipAutoScroll.current = false;
    pendingScrollToUnread.current = true;
    cancelPendingScrolls();
    setUnreadBannerCount(0);
    setFirstUnreadId(null);
  }, [chatId]);

  // Handle scroll after new messages arrive (for subsequent messages, not initial load)
  useEffect(() => {
    if (initialScrollDone.current && messages.length > prevMessageCount.current && prevMessageCount.current > 0) {
      if (userSentMessage.current) {
        skipAutoScroll.current = false;
        scrollToBottom();
        userSentMessage.current = false;
      } else {
        const container = messagesEndRef.current?.parentElement;
        if (container) {
          const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
          if (isNearBottom) scrollToBottom();
        }
      }
    }
    prevMessageCount.current = messages.length;
  }, [messages.length]);

  // Scroll listener to clear unread banner when user reaches bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      if (isAtBottom && unreadBannerCount > 0) {
        setUnreadBannerCount(0);
        setFirstUnreadId(null);
      }
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [unreadBannerCount]);

  // Fetch group members for mentions
  useEffect(() => {
    if (chat?.chat_type === 'group' && chat?.participants) {
      fetchGroupMembersWithStatus();
    }
  }, [chat]);

  // Refresh messages when WebSocket reconnects to catch missed updates
  const prevConnected = useRef(false);
  useEffect(() => {
    if (connected && !prevConnected.current && chatId) {
      fetchMessages();
    }
    prevConnected.current = connected;
  }, [connected]);

  // Refresh messages when app/tab regains focus (catches missed WS events)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && chatId) {
        fetchMessages();
        markMessagesAsRead();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [chatId]);

  // Periodic polling fallback for unreliable WebSocket (production WS can drop messages)
  useEffect(() => {
    if (!chatId) return;
    const pollInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchMessages();
      }
    }, 8000); // Poll every 8 seconds
    return () => clearInterval(pollInterval);
  }, [chatId]);

  const fetchGroupMembersWithStatus = async () => {
    if (!chat?.id) return;
    try {
      const response = await axios.get(`${API}/chats/${chat.id}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch group members:', error);
      // Fallback to old method
      if (chat?.participants) {
        const members = await Promise.all(
          chat.participants.filter(id => id !== user.id).map(async (userId) => {
            const res = await axios.get(`${API}/users/${userId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            return res.data;
          })
        );
        setGroupMembers(members);
      }
    }
  };

  // Delete message handler
  const handleDeleteMessage = async (messageId, forEveryone = false) => {
    try {
      await axios.delete(`${API}/messages/${messageId}?delete_for_everyone=${forEveryone}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (forEveryone) {
        // Update local state to show "This message was deleted"
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, content: 'This message was deleted', deleted_for_everyone: true }
            : msg
        ));
        toast.success('Message deleted for everyone');
      } else {
        // Remove from local state
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        toast.success('Message deleted');
      }
      setShowMessageMenu(null);
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  // Get message read/delivered info
  const handleGetMessageInfo = async (messageId) => {
    try {
      const response = await axios.get(`${API}/messages/${messageId}/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowMessageInfo(response.data);
    } catch (error) {
      console.error('Failed to get message info:', error);
      toast.error('Failed to get message info');
    }
  };

  // Get all incomplete task IDs from messages
  const getIncompleteTasks = useCallback(() => {
    return messages
      .filter(msg => msg.task_id && msg.content?.startsWith('☑️'))
      .map(msg => msg.task_id);
  }, [messages]);

  // Handle select all tasks toggle
  const handleSelectAllTasks = () => {
    if (selectAllTasks) {
      setSelectedTasks([]);
      setSelectAllTasks(false);
    } else {
      const incompleteTasks = getIncompleteTasks();
      setSelectedTasks(incompleteTasks);
      setSelectAllTasks(true);
    }
  };

  // Handle individual task selection
  const handleTaskSelect = (taskId) => {
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      } else {
        return [...prev, taskId];
      }
    });
  };

  // Bulk complete selected tasks
  const handleBulkCompleteTasks = async () => {
    if (selectedTasks.length === 0) return;
    
    setBulkCompleting(true);
    try {
      await axios.post(`${API}/tasks/bulk-complete`, selectedTasks, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${selectedTasks.length} task(s) marked complete!`);
      setSelectedTasks([]);
      setSelectAllTasks(false);
      fetchMessages(); // Refresh to show updated tasks
    } catch (error) {
      console.error('Failed to complete tasks:', error);
      toast.error('Failed to complete tasks');
    } finally {
      setBulkCompleting(false);
    }
  };

  // Clear chat messages
  const handleClearChat = async () => {
    setClearingChat(true);
    try {
      await axios.delete(`${API}/chats/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages([]);
      toast.success('Chat cleared');
      setShowChatMenu(false);
    } catch (error) {
      console.error('Failed to clear chat:', error);
      toast.error('Failed to clear chat');
    } finally {
      setClearingChat(false);
    }
  };

  // Mark messages as read when viewing (debounced to avoid rapid-fire API calls)
  const markReadTimeoutRef = useRef(null);
  const hasUnreadMessages = useRef(false);
  useEffect(() => {
    if (chat && messages.length > 0) {
      // Check if there are actually unread messages from others
      const unread = messages.some(m => m.sender_id !== user.id && !m.read_by?.includes(user.id));
      hasUnreadMessages.current = unread;
      if (unread && document.visibilityState === 'visible') {
        if (markReadTimeoutRef.current) clearTimeout(markReadTimeoutRef.current);
        markReadTimeoutRef.current = setTimeout(() => {
          markMessagesAsRead();
        }, 1500);
      }
    }
    return () => { if (markReadTimeoutRef.current) clearTimeout(markReadTimeoutRef.current); };
  }, [chat, messages.length]);

  const markMessagesAsRead = async () => {
    try {
      await axios.post(`${API}/messages/bulk-read?chat_id=${chatId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onChatUpdated();
      // Don't clear unread banner here - let scroll handler do it
      // Clear phone notifications
      try {
        if (window.Capacitor?.isNativePlatform()) {
          const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
          await FirebaseMessaging.removeAllDeliveredNotifications();
        }
      } catch {}
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  };

  const handleTaskUpdated = (taskId) => {
    // Refresh messages to show updated task status
    fetchMessages();
  };

  // Scroll to a specific task message in the chat
  const scrollToTask = (taskId) => {
    const taskMsg = messages.find(m => m.task_id === taskId);
    if (taskMsg) {
      const el = document.querySelector(`[data-testid="message-${taskMsg.id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedMessageId(taskMsg.id);
        setTimeout(() => setHighlightedMessageId(null), 2500);
        return;
      }
    }
    toast.info('This task was created in another chat');
  };

  // scrollToBottom is defined above

  const fetchChat = async () => {
    try {
      const response = await axios.get(`${API}/chats/${chatId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChat(response.data);
      
      // Fetch other user info for direct chats
      if (response.data.chat_type === 'direct') {
        const otherUserId = response.data.participants.find(id => id !== user.id);
        if (otherUserId) {
          const userResponse = await axios.get(`${API}/users/${otherUserId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setChat(prev => ({ ...prev, other_user: userResponse.data }));
        }
      }
      
      // Fetch wallpaper
      axios.get(`${API}/chats/${chatId}/wallpaper`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setChatWallpaper(res.data.wallpaper || 'default'))
        .catch(() => {});
    } catch (error) {
      console.error('Failed to fetch chat:', error);
      toast.error('Failed to load chat');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/chats/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out messages deleted for current user
      const filteredMessages = response.data.filter(msg => 
        !msg.deleted_for?.includes(user.id)
      );
      setMessages(filteredMessages);
      // Decrypt messages that are encrypted
      decryptMessages(filteredMessages);
      
      // Handle initial scroll-to-unread on first load of this chat
      if (pendingScrollToUnread.current) {
        pendingScrollToUnread.current = false;
        initialScrollDone.current = true;
        const unreadMsgs = filteredMessages.filter(m => m.sender_id !== user.id && !m.read_by?.includes(user.id));
        if (unreadMsgs.length > 0) {
          setUnreadBannerCount(unreadMsgs.length);
          setFirstUnreadId(unreadMsgs[0].id);
          skipAutoScroll.current = true;
          cancelPendingScrolls();
          const targetId = unreadMsgs[0].id;
          const scrollToUnread = () => {
            const bannerEl = document.querySelector('[data-testid="unread-banner"]');
            const firstMsgEl = document.querySelector(`[data-testid="message-${targetId}"]`);
            const target = bannerEl || firstMsgEl;
            if (target) {
              target.scrollIntoView({ behavior: 'instant', block: 'start' });
            }
          };
          // Schedule scroll after React renders the DOM with new messages
          requestAnimationFrame(() => { scrollToUnread(); setTimeout(scrollToUnread, 200); setTimeout(scrollToUnread, 500); setTimeout(() => { skipAutoScroll.current = false; }, 1000); });
        } else {
          setUnreadBannerCount(0);
          setFirstUnreadId(null);
          scrollToBottom();
        }
      }
      
      // Bulk mark undelivered messages as delivered (single API call)
      const hasUndelivered = filteredMessages.some(msg => 
        msg.sender_id !== user.id && 
        msg.sender_id !== 'system' && 
        !msg.delivered_to?.includes(user.id)
      );
      if (hasUndelivered) {
        axios.post(`${API}/messages/bulk-delivered?chat_id=${chatId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const decryptMessages = async (msgs) => {
    const decrypted = { ...decryptedMessages };
    for (const msg of msgs) {
      if (msg.encrypted && msg.encrypted_content && !decrypted[msg.id]) {
        const decryptedContent = await decrypt(msg);
        decrypted[msg.id] = decryptedContent;
      }
    }
    setDecryptedMessages(decrypted);
  };

  // Format message text with markdown support
  const formatMessageText = (text) => {
    if (!text) return text;
    
    // Check for numbered checkbox pattern: 1. [ ] text
    let formatted = text.replace(/^(\d+)\.\s*\[( )\]\s*(.+?)$/gm, (match, num, check, content) => {
      return `<div class="flex items-start gap-2 my-1"><span class="text-gray-400 text-xs flex-shrink-0 w-4">${num}.</span><div class="w-4 h-4 rounded border-2 border-gray-300 flex-shrink-0 mt-0.5"></div><span>${content}</span></div>`;
    });
    formatted = formatted.replace(/^(\d+)\.\s*\[([xX])\]\s*(.+?)$/gm, (match, num, check, content) => {
      return `<div class="flex items-start gap-2 my-1"><span class="text-gray-400 text-xs flex-shrink-0 w-4">${num}.</span><div class="w-4 h-4 rounded border-2 bg-green-500 border-green-500 flex items-center justify-center flex-shrink-0 mt-0.5"><svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path></svg></div><span class="line-through text-gray-500">${content}</span></div>`;
    });
    
    // Check for standalone checkbox pattern [ ] or [x]
    formatted = formatted.replace(/\[( )\]\s*(.+?)(?=\n|$)/g, (match, check, content) => {
      return `<div class="flex items-start gap-2 my-1"><div class="w-4 h-4 rounded border-2 border-gray-300 flex-shrink-0 mt-0.5"></div><span>${content}</span></div>`;
    });
    formatted = formatted.replace(/\[([xX])\]\s*(.+?)(?=\n|$)/g, (match, check, content) => {
      return `<div class="flex items-start gap-2 my-1"><div class="w-4 h-4 rounded border-2 bg-green-500 border-green-500 flex items-center justify-center flex-shrink-0 mt-0.5"><svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path></svg></div><span class="line-through text-gray-500">${content}</span></div>`;
    });
    
    // Convert **bold** to <strong>
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Convert *italic* or _italic_ to <em>
    formatted = formatted.replace(/\*(.+?)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/_(.+?)_/g, '<em>$1</em>');
    // Convert ~~strikethrough~~ to <del>
    formatted = formatted.replace(/~~(.+?)~~/g, '<del>$1</del>');
    // Convert `code` to <code>
    formatted = formatted.replace(/`(.+?)`/g, '<code class="bg-gray-200 px-1 rounded text-sm">$1</code>');
    
    // Bullet point lists
    formatted = formatted.replace(/^(\s*)([-•*])\s+(.+)$/gm, (match, indent, bullet, content) => {
      const paddingClass = indent.length > 0 ? `pl-${Math.min(indent.length, 8)}` : '';
      return `<div class="flex gap-2 ${paddingClass}"><span class="text-gray-500 flex-shrink-0">•</span><span>${content}</span></div>`;
    });
    
    // Convert newlines to <br> tags for proper display
    formatted = formatted.replace(/\n/g, '<br>');
    
    // Highlight @mentions
    formatted = formatted.replace(/@(\w[\w\s]*?)(?=\s|$|<br>)/g, '<span class="text-blue-500 font-medium cursor-pointer">@$1</span>');
    
    // Auto-link URLs
    formatted = formatted.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-500 underline break-all">$1</a>');
    
    return formatted;
  };

  // Get the recipient user ID for encryption
  const getRecipientId = () => {
    if (!chat || chat.chat_type !== 'direct') return null;
    return chat.participants.find(p => p !== user.id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    // Handle message editing
    if (editingMessageId) {
      try {
        await axios.put(`${API}/messages/${editingMessageId}`, { content: newMessage.trim() }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Message edited');
        setMessages(prev => prev.map(m => m.id === editingMessageId ? { ...m, content: newMessage.trim(), edited: true } : m));
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to edit');
      }
      setEditingMessageId(null);
      setEditingContent('');
      setNewMessage('');
      return;
    }

    setSending(true);
    sendStopTyping(chatId);
    userSentMessage.current = true;
    // Check if this is an AI buddy chat
    const isAiBuddyChat = chat?.system_chat_type === 'ai_buddy';

    try {
      if (isAiBuddyChat) {
        // Show user message immediately
        const tempUserMsgId = `temp_${Date.now()}`;
        const userMsgContent = newMessage;
        const tempUserMsg = {
          id: tempUserMsgId,
          chat_id: chatId,
          sender_id: user.id,
          content: userMsgContent,
          timestamp: new Date().toISOString(),
          read_by: [user.id],
          delivered_to: [user.id]
        };
        setMessages(prev => [...prev, tempUserMsg]);
        setNewMessage('');
        if (textareaRef.current) {
          textareaRef.current.style.height = '24px';
        }
        
        // Show AI typing indicator
        const aiTypingMsg = {
          id: 'ai_typing',
          chat_id: chatId,
          sender_id: 'ai_buddy',
          content: '...',
          timestamp: new Date().toISOString(),
          isTyping: true
        };
        setMessages(prev => [...prev, aiTypingMsg]);
        
        // Send to AI endpoint
        const response = await axios.post(
          `${API}/ai/chat`,
          { content: userMsgContent, chat_id: chatId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Remove typing indicator and temp message, add real messages
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== tempUserMsgId && m.id !== 'ai_typing');
          const newMsgs = [...filtered];
          if (response.data.user_message) {
            newMsgs.push(response.data.user_message);
          }
          if (response.data.ai_response) {
            newMsgs.push(response.data.ai_response);
          }
          return newMsgs;
        });
        
        // Notify parent to refresh chat list (updates last message preview)
        onChatUpdated();
      } else {
        // Normal message flow
        let messagePayload = {
          chat_id: chatId,
          content: newMessage,
          message_type: 'text',
          encrypted: false,
          mentions: selectedMentions,
          reply_to: replyToMessage?.id || null
        };

        // Try to encrypt if enabled and it's a direct chat
        if (encryptionEnabled && chat?.chat_type === 'direct') {
          const recipientId = getRecipientId();
          if (recipientId) {
            const encryptedData = await encrypt(newMessage, recipientId);
            if (encryptedData.encrypted) {
              messagePayload = {
                chat_id: chatId,
                content: encryptedData.content,
                message_type: 'text',
                encrypted: true,
                encrypted_content: encryptedData.encrypted_content,
                mentions: selectedMentions
              };
            }
          }
        }

        const response = await axios.post(
          `${API}/messages`,
          messagePayload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const sentMessage = response.data;
        setMessages(prev => [...prev, sentMessage]);
        setReplyToMessage(null);
        
        // Slash command feedback
        const trimmed = newMessage.trim();
        if (trimmed.startsWith('/task ') || trimmed.startsWith('/ytask ')) {
          toast.success('Task created!');
          // Refresh to show task in My Tasks
          if (onChatUpdated) onChatUpdated();
        } else if (trimmed.startsWith('/remind ')) {
          toast.success('Reminder set!');
        } else if (trimmed.startsWith('/poll ')) {
          toast.success('Poll created!');
        }
        
        // Store decrypted content for our own encrypted messages
        if (sentMessage.encrypted) {
          setDecryptedMessages(prev => ({
            ...prev,
            [sentMessage.id]: newMessage
          }));
        }
        
        setNewMessage('');
        setSelectedMentions([]);
        // Reset textarea height
        if (textareaRef.current) {
          textareaRef.current.style.height = '24px';
        }
        
        // Notify parent to refresh chat list
        onChatUpdated();
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Add optimistic message with failed state for retry
      const failedMsg = {
        id: `failed_${Date.now()}`,
        chat_id: chatId,
        sender_id: user.id,
        content: newMessage,
        message_type: 'text',
        timestamp: new Date().toISOString(),
        _failed: true,
        _payload: { chat_id: chatId, content: newMessage, message_type: 'text', mentions: selectedMentions, reply_to: replyToMessage?.id || null }
      };
      setMessages(prev => [...prev, failedMsg]);
      setNewMessage('');
      if (textareaRef.current) textareaRef.current.style.height = '24px';
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 100MB.');
      return;
    }

    setShowAttachMenu(false);
    setPreviewFile(file);
    setPreviewCaption('');

    // Create preview URL for images and videos
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      // Auto-open image editor for images
      if (file.type.startsWith('image/')) {
        setShowImageEditor(true);
      }
    } else {
      setPreviewUrl(null);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.removeAttribute('capture');
      fileInputRef.current.accept = 'image/*,video/*,audio/*,.pdf,.doc,.docx';
    }
  };

  const cancelPreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewFile(null);
    setPreviewUrl(null);
    setPreviewCaption('');
  };

  const retryMessage = async (failedMsg) => {
    try {
      setMessages(prev => prev.filter(m => m.id !== failedMsg.id));
      const payload = failedMsg._payload || { chat_id: chatId, content: failedMsg.content, message_type: failedMsg.message_type };
      if (failedMsg.media_url) payload.media_url = failedMsg.media_url;
      const response = await axios.post(`${API}/messages`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(prev => [...prev, response.data]);
      toast.success('Message sent!');
      onChatUpdated();
    } catch {
      setMessages(prev => [...prev, failedMsg]);
      toast.error('Still failed to send');
    }
  };

  const sendMediaMessage = async () => {
    if (!previewFile) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', previewFile);

      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      const { url, file_type } = uploadResponse.data;
      // Store RELATIVE url in DB - each client resolves using its own BACKEND_URL
      // This prevents mixed content issues (HTTP APK vs HTTPS webview)
      const mediaUrl = url.startsWith('http') ? url : url;

      // Send message with media and optional caption
      const messagePayload = {
        chat_id: chatId,
        content: previewCaption || (file_type === 'image' ? '📷 Photo' : file_type === 'video' ? '🎥 Video' : file_type === 'audio' ? '🎵 Audio' : '📎 File'),
        message_type: file_type,
        media_url: mediaUrl,
        mentions: []
      };

      const response = await axios.post(`${API}/messages`, messagePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages(prev => [...prev, response.data]);
      toast.success('Media sent successfully!');
      
      // Clean up preview
      cancelPreview();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji.native);
    setShowEmojiPicker(false);
  };

  // Handle paste event for images
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          // Set preview
          setPreviewFile(file);
          setPreviewUrl(URL.createObjectURL(file));
          toast.success('Image pasted! Add a caption and send.');
        }
        break;
      }
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Check file size (100MB limit)
      if (file.size > 100 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 100MB.');
        return;
      }
      
      setPreviewFile(file);
      setPreviewCaption('');
      
      // Create preview URL for images and videos
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside the emoji picker portal or the trigger button
      const isInsidePicker = event.target.closest('[data-emoji-picker]');
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target) && !isInsidePicker) {
        setShowEmojiPicker(false);
      }
    };
    
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Close all menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside any menu trigger or dropdown
      const isMenuTrigger = event.target.closest('[data-menu-trigger]');
      const isMenuDropdown = event.target.closest('[data-menu-dropdown]');
      
      if (!isMenuTrigger && !isMenuDropdown) {
        closeAllMenus();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [closeAllMenus]);

  // Don't auto-focus on mobile (prevents keyboard popup), focus on desktop only
  useEffect(() => {
    if (chatId && textareaRef.current && window.innerWidth >= 768) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [chatId]);

  // Fetch contact profile data for sidebar
  useEffect(() => {
    if (viewingContactProfile) {
      const fetchContactProfile = async () => {
        try {
          const [userRes, groupsRes, mediaRes] = await Promise.all([
            axios.get(`${API}/users/${viewingContactProfile.userId}`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${API}/users/${viewingContactProfile.userId}/common-groups`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: [] })),
            axios.get(`${API}/chats/${viewingContactProfile.chatId}/media`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: { images: [], videos: [], documents: [], links: [] } }))
          ]);
          setContactProfileData(userRes.data);
          setContactCommonGroups(groupsRes.data);
          setContactSharedMedia(mediaRes.data);
        } catch (error) {
          console.error('Failed to fetch contact profile:', error);
          toast.error('Failed to load contact info');
          setViewingContactProfile(null);
        }
      };
      fetchContactProfile();
    } else {
      setContactProfileData(null);
      setContactCommonGroups([]);
      setContactSharedMedia({ images: [], videos: [], documents: [], links: [] });
    }
  }, [viewingContactProfile, token]);

  const insertMention = (member) => {
    const lastAtIndex = newMessage.lastIndexOf('@');
    const newText = newMessage.slice(0, lastAtIndex) + `@${member.display_name} `;
    setNewMessage(newText);
    setSelectedMentions([...selectedMentions, member.id]);
    setShowMentionList(false);
  };

  const filteredMembers = groupMembers.filter(member =>
    member.display_name.toLowerCase().includes(mentionSearch) ||
    member.username.toLowerCase().includes(mentionSearch)
  );

  const handleTyping = () => {
    sendTyping(chatId);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      sendStopTyping(chatId);
    }, 1000);
  };

  const initiateCall = async (callType) => {
    if (!chat || chat.chat_type !== 'direct') {
      toast.error('Calls are only available for direct chats');
      return;
    }

    const otherUserId = chat.participants.find(id => id !== user.id);
    if (!otherUserId) {
      toast.error('Cannot find recipient');
      return;
    }

    try {
      const response = await axios.post(`${API}/calls/initiate`, {
        to_user_id: otherUserId,
        call_type: callType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate(`/call/${response.data.call_id}?type=${callType}&userId=${otherUserId}`);
    } catch (error) {
      toast.error('Failed to initiate call');
    }
  };

  const formatMessageTime = (timestamp) => {
    try {
      return format(new Date(timestamp), 'HH:mm');
    } catch {
      return '';
    }
  };

  // Get display name - handle system chats, direct chats, and group chats
  const displayName = chat?.system_chat_type 
    ? chat?.name  // System chats have a 'name' field
    : chat?.chat_type === 'direct' 
      ? chat?.other_user?.display_name || 'Unknown'
      : chat?.group_name || 'Group Chat';

  const isTyping = typingUsers[chatId] && Object.keys(typingUsers[chatId]).length > 0;

  // Get displayed content for a message (decrypted if encrypted)
  const getMessageContent = (message) => {
    if (message.encrypted) {
      return decryptedMessages[message.id] || message.content;
    }
    return message.content;
  };

  // Scroll to a specific message (for quoted message navigation)
  const scrollToMessage = (messageId) => {
    const element = document.querySelector(`[data-testid="message-${messageId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Golden pulse highlight effect
      element.style.transition = 'box-shadow 0.3s ease, background-color 0.3s ease';
      element.style.boxShadow = '0 0 0 3px rgba(212,175,55,0.6), 0 0 20px rgba(212,175,55,0.3)';
      element.style.backgroundColor = 'rgba(212,175,55,0.08)';
      element.style.borderRadius = '12px';
      setTimeout(() => {
        element.style.boxShadow = 'none';
        element.style.backgroundColor = '';
      }, 2500);
    }
  };

  // Handle highlight from task source navigation
  useEffect(() => {
    const highlightId = location.state?.highlightMessage;
    if (highlightId && messages.length > 0) {
      const tryHighlight = (attempt = 0) => {
        const element = document.querySelector(`[data-testid="message-${highlightId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.style.transition = 'box-shadow 0.3s ease, background-color 0.3s ease';
          element.style.boxShadow = '0 0 0 3px rgba(212,175,55,0.6), 0 0 20px rgba(212,175,55,0.3)';
          element.style.backgroundColor = 'rgba(212,175,55,0.15)';
          element.style.borderRadius = '12px';
          setTimeout(() => {
            element.style.boxShadow = 'none';
            element.style.backgroundColor = '';
          }, 3000);
        } else if (attempt < 5) {
          setTimeout(() => tryHighlight(attempt + 1), 500);
        }
      };
      setTimeout(() => tryHighlight(), 300);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.highlightMessage, messages.length]);

  // Render message content with checkbox support
  const renderMessageContent = (content, messageId) => {
    if (!content) return null;
    
    // Check for checkbox pattern [ ] or [x] or [X]
    const checkboxRegex = /\[([ xX])\]\s*(.+?)(?=\n|$)/g;
    const hasCheckboxes = checkboxRegex.test(content);
    
    if (hasCheckboxes) {
      const lines = content.split('\n');
      return (
        <div className="space-y-1">
          {lines.map((line, idx) => {
            const match = line.match(/^\[([ xX])\]\s*(.+)$/);
            if (match) {
              const isChecked = match[1].toLowerCase() === 'x';
              const text = match[2];
              return (
                <div key={idx} className="flex items-start gap-2">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    isChecked 
                      ? 'bg-[#D4AF37] border-[#D4AF37] text-white' 
                      : 'border-gray-300'
                  }`}>
                    {isChecked && <Check size={12} />}
                  </div>
                  <span className={isChecked ? 'line-through text-gray-500' : ''}>
                    {text}
                  </span>
                </div>
              );
            }
            return <div key={idx}>{line}</div>;
          })}
        </div>
      );
    }
    
    // Auto-indent lists (detect numbered or bulleted lists)
    const listRegex = /^(\s*)(\d+\.|[-•*])\s+(.+)$/gm;
    if (listRegex.test(content)) {
      const lines = content.split('\n');
      return (
        <div className="space-y-0.5">
          {lines.map((line, idx) => {
            const listMatch = line.match(/^(\s*)(\d+\.|[-•*])\s+(.+)$/);
            if (listMatch) {
              const indent = listMatch[1].length;
              const bullet = listMatch[2];
              const text = listMatch[3];
              const paddingLeft = Math.min(indent * 4, 48); // Max 48px indent
              return (
                <div key={idx} style={{ paddingLeft: `${paddingLeft}px` }} className="flex gap-2">
                  <span className="text-gray-500 flex-shrink-0">{bullet}</span>
                  <span>{text}</span>
                </div>
              );
            }
            return <div key={idx}>{line}</div>;
          })}
        </div>
      );
    }
    
    // Default: preserve newlines
    return content.split('\n').map((line, idx) => (
      <span key={idx}>
        {line}
        {idx < content.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  // Count online members
  const onlineMembersCount = groupMembers.filter(m => m.is_online).length;
  
  // Fetch other user's online/last-seen status for direct chats
  const [otherUserStatus, setOtherUserStatus] = React.useState(null);
  React.useEffect(() => {
    if (chat?.chat_type !== 'direct' || !chat?.other_user?.id) return;
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/users/${chat.other_user.id}/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOtherUserStatus(res.data);
      } catch {}
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000); // Refresh every 5s
    return () => clearInterval(interval);
  }, [chat?.other_user?.id, chat?.chat_type]);

  // Listen for real-time status changes via WebSocket
  React.useEffect(() => {
    if (!addMessageHandler) return;
    const handler = (data) => {
      if (data.type === 'user_status_changed') {
        // Update other user status in direct chats
        if (chat?.chat_type === 'direct' && chat?.other_user?.id === data.user_id) {
          setOtherUserStatus({ is_online: data.is_online, last_seen: data.last_seen, hidden: false });
        }
        // Update group member status
        if (chat?.chat_type === 'group') {
          setGroupMembers(prev => prev.map(m =>
            m.id === data.user_id ? { ...m, is_online: data.is_online } : m
          ));
        }
      }
    };
    return addMessageHandler(handler);
  }, [addMessageHandler, chat?.other_user?.id, chat?.chat_type]);

  const getOnlineStatusText = () => {
    if (!otherUserStatus) return '';
    if (otherUserStatus.is_online) return 'online';
    if (otherUserStatus.last_seen) {
      const lastSeen = new Date(otherUserStatus.last_seen);
      const now = new Date();
      const diff = now - lastSeen;
      if (diff < 60000) return 'last seen just now';
      if (diff < 3600000) return `last seen ${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `last seen ${Math.floor(diff / 3600000)}h ago`;
      return `last seen ${lastSeen.toLocaleDateString()}`;
    }
    return 'offline';
  };

  // Check if AI buddy chat
  const isAiBuddyChat = chat?.system_chat_type === 'ai_buddy';

  return (
    <div 
      className={`${isEmbedded ? 'h-full' : 'min-h-screen'} ${chatWallpaper !== 'default' ? `wallpaper-${chatWallpaper}` : 'chat-pattern-gold'} flex flex-col relative`} 
      data-testid="chat-interface"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag & Drop Overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-[#D4AF37]/20 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-dashed border-[#D4AF37]">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-800">Drop file to send</p>
              <p className="text-sm text-gray-500 mt-1">Images, videos, documents up to 100MB</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className={`${isEmbedded ? '' : 'sticky top-0'} z-20`}>

      <SkyHeader>
        <div className={`${isEmbedded ? 'p-3' : 'p-4'} safe-area-top`}>
          <div className="flex items-center gap-3">
            {/* Only show back button on mobile/standalone */}
            {!isEmbedded && (
              <button
                data-testid="back-btn"
                onClick={() => navigate('/chats')}
                className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <ArrowLeft size={20} className="text-white" />
              </button>
            )}

            <div 
              className="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => {
                if (isGroupChat) {
                  setGroupMediaLoaded(false);
                  setGroupSharedMedia({ images: [], videos: [], documents: [], links: [] });
                  setShowGroupInfo(true);
                  // Auto-load group media
                  axios.get(`${API}/chats/${chatId}/media`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(res => { setGroupSharedMedia(res.data); setGroupMediaLoaded(true); })
                    .catch(() => {});
                } else if (!isSystemChat && chat?.other_user) {
                  setViewingContactProfile({ userId: chat.other_user.id, chatId });
                }
              }}
              data-testid="chat-header-info"
            >
              <div className="relative">
                {!isAiBuddyChat && !isGroupChat && chat?.other_user?.avatar_url ? (
                  <img 
                    src={resolveAvatarUrl(chat.other_user.avatar_url)} 
                    alt={displayName} 
                    className="w-10 h-10 rounded-full object-cover cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); setExpandedAvatar(resolveAvatarUrl(chat.other_user.avatar_url)); }}
                    onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium cursor-pointer ${
                    isAiBuddyChat 
                      ? 'bg-gradient-to-br from-purple-500 to-indigo-600' 
                      : 'bg-gradient-to-br from-[#D4AF37] to-[#C5A059]'
                  }`} 
                  style={!isAiBuddyChat && !isGroupChat && chat?.other_user?.avatar_url ? { display: 'none' } : {}}
                  onClick={(e) => {
                    if (!isAiBuddyChat && !isGroupChat && chat?.other_user?.avatar_url) {
                      e.stopPropagation();
                      setExpandedAvatar(resolveAvatarUrl(chat.other_user.avatar_url));
                    }
                  }}
                >
                  {isAiBuddyChat ? '🧠' : isGroupChat ? <Users size={20} /> : displayName?.charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="font-medium text-white drop-shadow-sm truncate text-sm">{displayName}</h2>
                  {chat?.disappearing_messages > 0 && (
                    <Clock size={14} className="text-[#D4AF37]" title="Disappearing messages on" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isTyping && (
                    <p className="text-xs text-primary">typing...</p>
                  )}
                  {chat?.chat_type === 'direct' && !isTyping && (
                    <p className={`text-xs ${otherUserStatus?.is_online ? 'text-green-400' : 'text-white/60'}`}>
                      {getOnlineStatusText()}
                    </p>
                  )}
                  {isGroupChat && !isTyping && (
                    <p className="text-xs text-white/60">
                      {onlineMembersCount > 0 ? `${onlineMembersCount} online` : `${groupMembers.length} members`}
                    </p>
                  )}
                  {encryptionEnabled && chat?.chat_type === 'direct' && (
                    <div className="flex items-center gap-1 text-green-600">
                      <Lock size={10} />
                      <span className="text-xs">Encrypted</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-2 flex-shrink-0">
              {/* Search in chat button */}
              <button
                data-testid="search-in-chat-btn"
                onClick={() => setShowInChatSearch(!showInChatSearch)}
                className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
                  showInChatSearch 
                    ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                    : 'hover:bg-secondary-DEFAULT'
                }`}
              >
                <Search size={20} className={showInChatSearch ? 'text-[#D4AF37]' : 'text-white'} />
              </button>
              <button
                data-testid="voice-call-btn"
                onClick={() => initiateCall('voice')}
                className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary-DEFAULT transition-colors"
              >
                <Phone size={20} className="text-white" />
              </button>
              <button
                data-testid="video-call-btn"
                onClick={() => initiateCall('video')}
                className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary-DEFAULT transition-colors"
              >
                <Video size={20} className="text-white" />
              </button>
              <div className="relative">
                <button
                  data-testid="more-btn"
                  data-menu-trigger
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAttachMenu(false);
                    setShowEmojiPicker(false);
                    if (!showChatMenu) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setChatMenuPos({ 
                        top: rect.bottom + 4, 
                        left: Math.max(8, Math.min(rect.right - 200, window.innerWidth - 208))
                      });
                    }
                    setShowChatMenu(!showChatMenu);
                  }}
                  className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary-DEFAULT transition-colors"
                >
                  <MoreVertical size={20} className="text-white" />
                </button>
                
                {/* Chat Menu Dropdown - portal */}
                {showChatMenu && chatMenuPos && createPortal(
                  <div 
                    data-menu-dropdown
                    className={`fixed w-48 rounded-xl shadow-lg border z-[9999] ${
                      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-border-DEFAULT'
                    }`}
                    style={{ top: chatMenuPos.top, left: chatMenuPos.left }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Mark all as read */}
                    <button
                      onClick={() => {
                        handleMarkAllRead();
                        setShowChatMenu(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm ${
                        isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'
                      }`}
                    >
                      <CheckCheck size={16} />
                      Mark all as read
                    </button>
                    
                    {/* Select messages */}
                    <button
                      onClick={() => {
                        setSelectMode(true);
                        setShowChatMenu(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm ${
                        isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'
                      }`}
                    >
                      <CheckSquare size={16} />
                      Select messages
                    </button>

                    {/* Scheduled messages */}
                    <button
                      onClick={() => {
                        setShowScheduleModal(true);
                        setShowChatMenu(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm ${
                        isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'
                      }`}
                      data-testid="menu-scheduled-messages"
                    >
                      <Clock size={16} />
                      Scheduled messages
                    </button>
                    
                    {/* Group-specific options */}
                    {isGroupChat && (
                      <>
                        <div className={`border-t my-1 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}></div>
                        
                        {/* Leave group (non-creator) */}
                        {chat?.created_by !== user.id && (
                          <button
                            onClick={() => {
                              handleLeaveGroup();
                              setShowChatMenu(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-orange-500 ${
                              isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                            }`}
                          >
                            <LogOut size={16} />
                            Exit group
                          </button>
                        )}
                        
                        {/* Delete group (creator only) */}
                        {chat?.created_by === user.id && (
                          <button
                            onClick={() => {
                              handleDeleteGroup();
                              setShowChatMenu(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-red-500 ${
                              isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                            }`}
                          >
                            <Trash2 size={16} />
                            Delete group
                          </button>
                        )}
                      </>
                    )}
                    
                    <div className={`border-t my-1 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}></div>
                    
                    {/* Wallpaper */}
                    <div className={`px-4 py-2`}>
                      <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Wallpaper</p>
                      <div className="flex gap-1.5 flex-wrap">
                        {[
                          { id: 'default', color: 'bg-[#EBE4D6]' },
                          { id: 'forest', color: 'bg-green-200' },
                          { id: 'ocean', color: 'bg-blue-200' },
                          { id: 'sunset', color: 'bg-orange-200' },
                          { id: 'lavender', color: 'bg-purple-200' },
                          { id: 'midnight', color: 'bg-gray-700' }
                        ].map(w => (
                          <button
                            key={w.id}
                            onClick={async () => {
                              setChatWallpaper(w.id);
                              try {
                                await axios.put(`${API}/chats/${chatId}/wallpaper`, { wallpaper: w.id }, { headers: { Authorization: `Bearer ${token}` } });
                              } catch {}
                              setShowChatMenu(false);
                            }}
                            className={`w-7 h-7 rounded-full ${w.color} border-2 transition-transform hover:scale-110 ${chatWallpaper === w.id ? 'border-[#D4AF37] scale-110' : 'border-transparent'}`}
                            title={w.id}
                          />
                        ))}
                      </div>
                    </div>
                    
                    <div className={`border-t my-1 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}></div>
                    
                    {/* Clear chat */}
                    <button
                      onClick={handleClearChat}
                      disabled={clearingChat}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-red-500 ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      } disabled:opacity-50`}
                      data-testid="clear-chat-btn"
                    >
                      <Trash2 size={16} />
                      {clearingChat ? 'Clearing...' : 'Clear chat'}
                    </button>
                  </div>,
                  document.body
                )}
              </div>
            </div>
          </div>
        </div>
      </SkyHeader>
      </div>

      {/* In-Chat Search Bar */}
      {showInChatSearch && (
        <div className={`sticky top-0 z-20 border-b ${
          isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-border-DEFAULT'
        }`}>
          <div className={`${isEmbedded ? 'p-3' : 'chat-container p-3'} flex items-center gap-3`}>
            <div className="relative flex-1">
              <Search size={18} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                value={inChatSearchQuery}
                onChange={(e) => {
                  setInChatSearchQuery(e.target.value);
                  searchInChat(e.target.value);
                }}
                placeholder="Search in this chat..."
                className={`w-full pl-10 pr-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' 
                    : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                }`}
                autoFocus
                data-testid="in-chat-search-input"
              />
            </div>
            <button
              onClick={() => {
                setShowInChatSearch(false);
                setInChatSearchQuery('');
                setInChatSearchResults([]);
              }}
              className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
            >
              <X size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            </button>
          </div>
          
          {/* Search Results */}
          {inChatSearchQuery && (
            <div className={`max-h-60 overflow-y-auto border-t ${
              isDarkMode ? 'border-gray-700' : 'border-gray-100'
            }`}>
              {searchingInChat ? (
                <div className={`p-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Searching...
                </div>
              ) : inChatSearchResults.length === 0 ? (
                <div className={`p-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No messages found
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {inChatSearchResults.map((msg) => (
                    <button
                      key={msg.id}
                      onClick={() => {
                        navigateToMessage(msg.id);
                        setShowInChatSearch(false);
                        setInChatSearchQuery('');
                        setInChatSearchResults([]);
                      }}
                      className={`w-full p-3 text-left transition-colors ${
                        isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                      }`}
                    >
                      <p className={`text-sm line-clamp-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {msg.content}
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {format(new Date(msg.timestamp), 'MMM d, h:mm a')}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Select Mode Bar */}
      {selectMode && (
        <div className={`sticky top-0 z-20 border-b ${
          isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-border-DEFAULT'
        }`}>
          <div className="chat-container p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setSelectMode(false);
                  setSelectedMessages([]);
                }}
                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
              >
                <X size={20} />
              </button>
              <span className={isDarkMode ? 'text-white' : 'text-text-primary'}>
                {selectedMessages.length} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={selectAllMessages}
                className={`px-3 py-1.5 text-sm rounded-lg ${
                  isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
                }`}
              >
                Select all
              </button>
              <button
                onClick={() => handleBulkDelete(false)}
                disabled={selectedMessages.length === 0}
                className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white disabled:opacity-50"
              >
                Delete for me
              </button>
              <button
                onClick={() => handleBulkDelete(true)}
                disabled={selectedMessages.length === 0}
                className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white disabled:opacity-50"
              >
                Delete for all
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Tasks Selection Bar (for Tasks chat) */}
      {isTasksChat && getIncompleteTasks().length > 0 && (
        <div className={`sticky top-[72px] z-10 border-b ${
          isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-border-DEFAULT'
        }`}>
          <div className="chat-container p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectAllTasks}
                  onChange={handleSelectAllTasks}
                  className="w-4 h-4 rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37]"
                  data-testid="select-all-tasks"
                />
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-text-secondary'}`}>
                  Select all ({getIncompleteTasks().length} pending)
                </span>
              </label>
            </div>
            {selectedTasks.length > 0 && (
              <button
                onClick={handleBulkCompleteTasks}
                disabled={bulkCompleting}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="bulk-complete-btn"
              >
                {bulkCompleting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Check size={16} />
                )}
                Complete {selectedTasks.length} task{selectedTasks.length > 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto scrollbar-hide px-4 py-6" data-testid="messages-container">
        <div className="w-full">
          {loading ? (
            <div className="space-y-4 py-4 animate-pulse">
              {[...Array(6)].map((_, i) => (
                <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'} px-4`}>
                  <div className={`rounded-2xl ${i % 2 === 0 ? 'rounded-tr-sm' : 'rounded-tl-sm'} ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-200/70'}`}
                    style={{ width: `${120 + Math.random() * 140}px`, height: `${32 + (i % 3) * 12}px` }} />
                </div>
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-secondary-DEFAULT flex items-center justify-center mx-auto mb-4">
                <Send size={28} className="text-text-tertiary" />
              </div>
              <p className="text-text-secondary">
                {isTasksChat ? 'No tasks yet. Create your first task!' : 'No messages yet. Start the conversation!'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message, index) => {
                const isSent = message.sender_id === user.id;
                const isSystemMessage = message.sender_id === 'system';
                
                // Date separator
                let showDateSeparator = false;
                let dateLabel = '';
                if (message.timestamp) {
                  const msgDate = new Date(message.timestamp);
                  const today = new Date();
                  const yesterday = new Date(today);
                  yesterday.setDate(yesterday.getDate() - 1);
                  
                  const prevMsg = index > 0 ? messages[index - 1] : null;
                  const prevDate = prevMsg?.timestamp ? new Date(prevMsg.timestamp) : null;
                  
                  if (!prevDate || msgDate.toDateString() !== prevDate.toDateString()) {
                    showDateSeparator = true;
                    if (msgDate.toDateString() === today.toDateString()) {
                      dateLabel = 'Today';
                    } else if (msgDate.toDateString() === yesterday.toDateString()) {
                      dateLabel = 'Yesterday';
                    } else {
                      dateLabel = format(msgDate, 'MMMM d, yyyy');
                    }
                  }
                }
                
                // AI typing indicator
                if (message.isTyping && message.sender_id === 'ai_buddy') {
                  return (
                    <React.Fragment key={message.id}>
                      {showDateSeparator && (
                        <div className="flex justify-center my-3">
                          <span className={`px-4 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white/80 text-gray-500 shadow-sm'}`}>{dateLabel}</span>
                        </div>
                      )}
                      {firstUnreadId === message.id && unreadBannerCount > 0 && (
                        <div className="flex justify-center my-2" data-testid="unread-banner">
                          <span className="px-4 py-1 rounded-full text-xs font-medium bg-[#D4AF37] text-white shadow-sm">{unreadBannerCount} UNREAD MESSAGE{unreadBannerCount > 1 ? 'S' : ''}</span>
                        </div>
                      )}
                      <div className="flex justify-start">
                        <div className="message-bubble-received">
                          <div className="flex items-center gap-1 py-2">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                }
                
                // For Tasks chat, render task summary messages specially
                if (isTasksChat && (message.message_type === 'task_summary' || message.content?.startsWith('📋'))) {
                  return (
                    <React.Fragment key={message.id}>
                      {showDateSeparator && (
                        <div className="flex justify-center my-3">
                          <span className={`px-4 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white/80 text-gray-500 shadow-sm'}`}>{dateLabel}</span>
                        </div>
                      )}
                      {firstUnreadId === message.id && unreadBannerCount > 0 && (
                        <div className="flex justify-center my-2" data-testid="unread-banner">
                          <span className="px-4 py-1 rounded-full text-xs font-medium bg-[#D4AF37] text-white shadow-sm">{unreadBannerCount} UNREAD MESSAGE{unreadBannerCount > 1 ? 'S' : ''}</span>
                        </div>
                      )}
                      <div data-testid={`message-${message.id}`} className="flex justify-center my-2">
                        <TaskSummaryMessage 
                          message={message} 
                          onScrollToTask={scrollToTask}
                        />
                      </div>
                    </React.Fragment>
                  );
                }
                
                // For Tasks chat, render task messages specially
                if (isTasksChat && (message.task_id || message.content?.startsWith('☑️') || message.content?.startsWith('✅'))) {
                  return (
                    <React.Fragment key={message.id}>
                      {showDateSeparator && (
                        <div className="flex justify-center my-3">
                          <span className={`px-4 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white/80 text-gray-500 shadow-sm'}`}>{dateLabel}</span>
                        </div>
                      )}
                      {firstUnreadId === message.id && unreadBannerCount > 0 && (
                        <div className="flex justify-center my-2" data-testid="unread-banner">
                          <span className="px-4 py-1 rounded-full text-xs font-medium bg-[#D4AF37] text-white shadow-sm">{unreadBannerCount} UNREAD MESSAGE{unreadBannerCount > 1 ? 'S' : ''}</span>
                        </div>
                      )}
                      <div data-testid={`message-${message.id}`} className={`transition-all duration-500 rounded-2xl ${
                        highlightedMessageId === message.id ? 'ring-2 ring-[#D4AF37] bg-[#D4AF37]/15 scale-[1.02] shadow-lg shadow-[#D4AF37]/20' : ''
                      }`}>
                        <TaskMessage 
                          message={message} 
                          onTaskUpdated={handleTaskUpdated}
                          onNavigateToChat={(sourceChatId, sourceMessageId) => {
                            if (sourceChatId) {
                              navigate(`/chat/${sourceChatId}`, { state: { highlightMessage: sourceMessageId } });
                            }
                          }}
                        />
                      </div>
                    </React.Fragment>
                  );
                }
                
                // Check if message was deleted for everyone
                const isDeletedForEveryone = message.deleted_for_everyone;
                
                return (
                  <React.Fragment key={message.id}>
                    {showDateSeparator && (
                      <div className="flex justify-center my-3">
                        <span className={`px-4 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white/80 text-gray-500 shadow-sm'}`}>{dateLabel}</span>
                      </div>
                    )}
                    {firstUnreadId === message.id && unreadBannerCount > 0 && (
                      <div className="flex justify-center my-2" data-testid="unread-banner">
                        <span className="px-4 py-1 rounded-full text-xs font-medium bg-[#D4AF37] text-white shadow-sm">{unreadBannerCount} UNREAD MESSAGE{unreadBannerCount > 1 ? 'S' : ''}</span>
                      </div>
                    )}
                  <div
                    data-testid={`message-${message.id}`}
                    data-message-id={message.id}
                    className={`flex ${isSystemMessage ? 'justify-center' : isSent ? 'justify-end' : 'justify-start'} relative mb-1 ${
                      highlightedMessageId === message.id ? 'bg-[#D4AF37]/25 ring-2 ring-[#D4AF37]/40 rounded-lg scale-[1.01]' : ''} ${
                      showMessageMenu === message.id ? (isDarkMode ? 'bg-gray-700/30' : 'bg-[#D4AF37]/10') + ' rounded-lg' : ''
                    }`}
                    style={{ willChange: 'transform' }}
                    onTouchStart={(e) => {
                      if (!isSystemMessage && !isDeletedForEveryone) {
                        handleLongPressStart(message.id, e);
                        handleTouchStart(e, message.id);
                      }
                    }}
                    onTouchMove={(e) => handleTouchMove(e, message.id)}
                    onTouchEnd={(e) => {
                      handleLongPressEnd();
                      handleTouchEnd(e, message.id);
                    }}
                    onTouchCancel={handleLongPressEnd}
                    onContextMenu={(e) => {
                      if (!isSystemMessage && !isDeletedForEveryone) {
                        // On touch devices use long press; on desktop allow native right-click for text selection
                        const isTouchDevice = window.matchMedia('(hover: none)').matches;
                        if (isTouchDevice) {
                          e.preventDefault();
                        }
                        const menuWidth = 200;
                        const menuHeight = 400;
                        const spaceBelow = window.innerHeight - e.clientY;
                        const flipUp = spaceBelow < menuHeight;
                        setMessageMenuPos({
                          top: flipUp ? Math.max(8, e.clientY - menuHeight) : e.clientY + 10,
                          left: Math.min(Math.max(8, e.clientX - menuWidth / 2), window.innerWidth - menuWidth - 8)
                        });
                        setShowMessageMenu(message.id);
                      }
                    }}
                  >
                    {/* Select mode checkbox */}
                    {selectMode && !isSystemMessage && (
                      <div className="flex items-center mr-2">
                        <input
                          type="checkbox"
                          checked={selectedMessages.includes(message.id)}
                          onChange={() => toggleMessageSelect(message.id)}
                          className="w-5 h-5 rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37]"
                        />
                      </div>
                    )}
                    
                    {/* Swipe reply icon (mobile) - positioned via direct DOM manipulation */}
                    {!isSystemMessage && !isDeletedForEveryone && (
                      <div data-swipe-icon className={`absolute ${isSent ? 'left-0' : 'right-0'} top-1/2 w-10 h-10 bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center rounded-full pointer-events-none`} style={{ opacity: 0, transform: 'translateY(-50%) scale(0.5)' }}>
                        <Forward size={18} className="rotate-180" />
                      </div>
                    )}
                    
                    {/* System message styling */}
                    {isSystemMessage ? (
                      <div className={`px-4 py-2 rounded-xl text-sm flex items-center gap-2 ${
                        message.system_type === 'missed_call'
                          ? isDarkMode ? 'bg-red-900/40 text-red-400 border border-red-800/50' : 'bg-red-50 text-red-600 border border-red-200'
                          : isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-text-secondary'
                      }`} style={{ whiteSpace: 'pre-line' }} data-testid={`system-msg-${message.id}`}>
                        {message.system_type === 'missed_call' && (
                          <div className="relative flex-shrink-0">
                            {message.call_type === 'video' ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                                <line x1="4" y1="4" x2="20" y2="20" className="text-red-500" strokeWidth="3"/>
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91"/>
                                <line x1="1" y1="1" x2="23" y2="23" strokeWidth="2.5"/>
                              </svg>
                            )}
                          </div>
                        )}
                        <div className="flex-1">
                          <span className="font-medium">{message.content}</span>
                          {message.system_type === 'missed_call' && message.timestamp && (
                            <span className="text-xs opacity-70 ml-1">
                              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        {message.system_type === 'missed_call' && (
                          <button
                            data-testid={`call-back-btn-${message.id}`}
                            onClick={() => initiateCall(message.call_type || 'voice')}
                            className={`flex-shrink-0 text-xs font-semibold px-2 py-1 rounded-lg border ${
                              isDarkMode ? 'border-red-500/50 text-red-400 hover:bg-red-900/30' : 'border-red-300 text-red-600 hover:bg-red-100'
                            } transition-colors`}
                          >
                            Call back
                          </button>
                        )}
                      </div>
                    ) : (
                    <div className="relative max-w-[85%] md:max-w-[70%] group">
                      <div className={`${isSent ? 'message-bubble-sent' : 'message-bubble-received'} ${message.media_url ? '!p-1' : ''}`}>
                        {/* Reply/Quote preview - WhatsApp style */}
                        {message.reply_to && (() => {
                          const repliedMsg = messages.find(m => m.id === message.reply_to);
                          const repliedSenderName = repliedMsg?.sender_id === user.id 
                            ? 'You' 
                            : (repliedMsg?.sender?.display_name || groupMembers.find(m => m.id === repliedMsg?.sender_id)?.display_name || chat?.other_user?.display_name || 'User');
                          return (
                            <div 
                              onClick={() => scrollToMessage(message.reply_to)}
                              className={`mb-2 p-2.5 rounded-lg cursor-pointer border-l-4 ${
                                isSent
                                  ? 'bg-[#C5A059]/20 border-[#D4AF37]'
                                  : isDarkMode 
                                    ? 'bg-gray-700/50 border-blue-400 hover:bg-gray-700' 
                                    : 'bg-blue-50/50 border-blue-400 hover:bg-blue-50'
                              }`}
                            >
                              <p className={`text-xs font-medium mb-0.5 ${isSent ? 'text-[#8B6914]' : 'text-blue-500'}`}>
                                {repliedSenderName}
                              </p>
                              <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {repliedMsg?.content?.slice(0, 60) || 'Original message'}
                              </p>
                            </div>
                          );
                        })()}
                        
                        {/* Dropdown Arrow Button - Top Right Corner */}
                        {!isDeletedForEveryone && !isSystemChat && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (showMessageMenu !== message.id) {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const menuWidth = 200;
                                const menuHeight = 400;
                                const spaceBelow = window.innerHeight - rect.bottom;
                                const flipUp = spaceBelow < menuHeight;
                                setMessageMenuPos({
                                  top: flipUp ? Math.max(8, rect.top - menuHeight) : rect.bottom + 4,
                                  left: isSent 
                                    ? Math.max(8, rect.left - menuWidth + rect.width) 
                                    : Math.min(rect.left, window.innerWidth - menuWidth - 8)
                                });
                              }
                              setShowMessageMenu(showMessageMenu === message.id ? null : message.id);
                            }}
                            className={`absolute -top-1 ${isSent ? '-left-6' : '-right-6'} w-6 h-6 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 z-10 ${
                              showMessageMenu === message.id ? 'opacity-100' : ''
                            } ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-100 shadow-sm border border-gray-200'}`}
                            data-testid={`message-menu-btn-${message.id}`}
                          >
                            <ChevronDown size={14} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
                          </button>
                        )}
                        
                        {/* Message Menu Dropdown - via portal */}
                        {showMessageMenu === message.id && messageMenuPos && createPortal(
                          <div 
                            data-menu-dropdown
                            className={`fixed w-auto min-w-[200px] max-w-[280px] rounded-xl shadow-lg border z-[9999] max-h-[70vh] overflow-y-auto ${
                              isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-border-DEFAULT'
                            }`}
                            style={{ 
                              top: Math.max(8, Math.min(messageMenuPos.top, window.innerHeight - 400)),
                              left: Math.max(8, Math.min(messageMenuPos.left, window.innerWidth - 220))
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Quick Reactions Row */}
                            <div className={`flex items-center gap-1 p-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                              {quickReactions.map(emoji => {
                                const hasReacted = message.reactions?.[emoji]?.includes(user.id);
                                return (
                                  <button
                                    key={emoji}
                                    onClick={() => handleReaction(message.id, emoji)}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-lg hover:bg-gray-100 transition-colors ${
                                      hasReacted ? 'bg-[#D4AF37]/20 scale-110' : ''
                                    } ${isDarkMode ? 'hover:bg-gray-700' : ''}`}
                                    title={hasReacted ? 'Remove reaction' : `React with ${emoji}`}
                                  >
                                    {emoji}
                                  </button>
                                );
                              })}
                            </div>
                            
                            {/* Reply */}
                            <button
                              onClick={() => { 
                                setReplyToMessage({ 
                                  id: message.id, 
                                  content: message.content?.slice(0, 80), 
                                  senderName: isSent ? 'You' : (message.sender?.display_name || groupMembers.find(m => m.id === message.sender_id)?.display_name || chat?.other_user?.display_name || 'User')
                                }); 
                                textareaRef.current?.focus(); 
                                setShowMessageMenu(null); 
                              }}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'}`}
                            >
                              <Forward size={16} className="rotate-180" /> Reply
                            </button>
                            
                            {/* Edit (own messages, within 15 min) */}
                            {isSent && (() => { const msgTime = new Date(message.timestamp); return (Date.now() - msgTime.getTime()) < 900000; })() && (
                              <button
                                onClick={() => {
                                  setEditingMessageId(message.id);
                                  setEditingContent(message.content);
                                  setNewMessage(message.content);
                                  setShowMessageMenu(null);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'}`}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                                Edit
                              </button>
                            )}
                            
                            {/* Copy */}
                            <button
                              onClick={() => copyMessage(message.content)}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'}`}
                            >
                              <Copy size={16} /> Copy
                            </button>
                            
                            {/* Star */}
                            <button
                              onClick={() => toggleStarMessage(message.id)}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'}`}
                            >
                              <Star size={16} className={starredMessages.includes(message.id) ? 'fill-yellow-400 text-yellow-400' : ''} />
                              {starredMessages.includes(message.id) ? 'Unstar' : 'Star'}
                            </button>
                            
                            {/* Forward */}
                            <button
                              onClick={() => openForwardModal(message)}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'}`}
                            >
                              <Forward size={16} /> Forward
                            </button>
                            
                            {/* Pin Message */}
                            <button
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/chats/${chatId}/pin-message/${message.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success('Message pinned');
                                } catch { toast.error('Failed to pin'); }
                                setShowMessageMenu(null);
                              }}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'}`}
                            >
                              <Pin size={16} /> Pin message
                            </button>
                            
                            {/* Select messages */}
                            <button
                              onClick={() => { setSelectMode(true); setSelectedMessages([message.id]); setShowMessageMenu(null); }}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'}`}
                            >
                              <CheckSquare size={16} /> Select
                            </button>
                            
                            <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`} />
                            
                            {/* Quick Task Creation */}
                            <button
                              onClick={async () => {
                                setShowMessageMenu(null);
                                try {
                                  await axios.post(`${API}/tasks`, {
                                    title: message.content?.slice(0, 100) || 'Task',
                                    message_id: message.id,
                                    chat_id: chatId,
                                    due_date: new Date().toISOString().split('T')[0],
                                    priority: 'medium'
                                  }, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success('Task created for you!');
                                } catch { toast.error('Failed to create task'); }
                              }}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'}`}
                            >
                              <CheckSquare size={16} /> Task for me
                            </button>
                            {/* Assign task to the other person */}
                            {chat?.chat_type === 'direct' && !isSystemChat && (
                              <button
                                onClick={async () => {
                                  setShowMessageMenu(null);
                                  const otherId = isSent 
                                    ? chat.participants?.find(p => p !== user.id) 
                                    : message.sender_id;
                                  if (!otherId) return;
                                  try {
                                    await axios.post(`${API}/tasks`, {
                                      title: message.content?.slice(0, 100) || 'Task',
                                      message_id: message.id,
                                      chat_id: chatId,
                                      assigned_to: otherId,
                                      due_date: new Date().toISOString().split('T')[0],
                                      priority: 'medium'
                                    }, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success(isSent ? 'Task assigned!' : 'Task created for sender!');
                                  } catch { toast.error('Failed to create task'); }
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm ${
                                  isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'
                                }`}
                              >
                                <CheckSquare size={16} />
                                {isSent ? `Task for ${chat?.other_user?.display_name?.split(' ')[0] || 'them'}` : 'Task for sender'}
                              </button>
                            )}
                            
                            {/* Set Reminder */}
                            <button
                              onClick={() => {
                                setSelectedMessage(message);
                                setShowReminderModal(true);
                                setShowMessageMenu(null);
                              }}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm ${
                                isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'
                              }`}
                            >
                              <Clock size={16} />
                              Set Reminder
                            </button>
                            
                            {/* Message info (for group chats) */}
                            {isGroupChat && isSent && (
                              <button
                                onClick={() => {
                                  handleGetMessageInfo(message.id);
                                  setShowMessageMenu(null);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm ${
                                  isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'
                                }`}
                              >
                                <Info size={16} />
                                Message info
                              </button>
                            )}
                            
                            {/* Divider */}
                            {isSent && <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`} />}
                            
                            {/* Delete for me */}
                            <button
                              onClick={() => handleDeleteMessage(message.id, false)}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm ${
                                isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-text-primary'
                              }`}
                            >
                              <Trash2 size={16} />
                              Delete for me
                            </button>
                            
                            {/* Delete for everyone (only sender or admin) */}
                            {(isSent || isAdmin) && (
                              <button
                                onClick={() => handleDeleteMessage(message.id, true)}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-red-500 ${
                                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                }`}
                              >
                                <Trash2 size={16} />
                                Delete for everyone
                              </button>
                            )}
                          </div>,
                          document.body
                        )}

                        {/* Poll Content */}
                        {message.poll_data && !isDeletedForEveryone && (
                          <div className="min-w-[200px] mb-1">
                            <p className="text-sm font-medium mb-2">{message.poll_data.question}</p>
                            {message.poll_data.options.map(opt => {
                              const totalVotes = message.poll_data.options.reduce((s, o) => s + (o.votes?.length || 0), 0);
                              const votes = opt.votes?.length || 0;
                              const pct = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
                              const voted = opt.votes?.includes(user.id);
                              return (
                                <button key={opt.id} onClick={async () => { try { const pr = await axios.post(`${API}/polls/${message.id}/vote`, { option_id: opt.id }, { headers: { Authorization: `Bearer ${token}` } }); setMessages(prev => prev.map(m => m.id === message.id ? { ...m, poll_data: pr.data.poll_data } : m)); } catch {} }} className={`w-full text-left p-2 mb-1 rounded-lg text-xs relative overflow-hidden ${voted ? 'font-medium' : ''}`}>
                                  <div className={`absolute inset-0 rounded-lg ${voted ? 'bg-[#D4AF37]/20' : 'bg-black/5'}`} style={{ width: `${pct}%` }} />
                                  <span className="relative">{opt.text} {totalVotes > 0 && <span className="text-gray-400 ml-1">{pct}%</span>}</span>
                                </button>
                              );
                            })}
                            <p className="text-[10px] text-gray-400 mt-1">{message.poll_data.options.reduce((s, o) => s + (o.votes?.length || 0), 0)} votes</p>
                          </div>
                        )}
                        {/* Media Content */}
                        {message.media_url && !isDeletedForEveryone && (
                          <div className="mb-2">
                            {message.message_type === 'image' ? (
                              <div className="relative w-[200px] h-[200px] rounded-lg overflow-hidden">
                                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} animate-pulse`} data-skeleton="true" />
                                <img 
                                  src={resolveMediaUrl(message.media_url)} 
                                  alt="Shared image" 
                                  className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity relative z-10"
                                  onClick={() => setLightboxMedia({ url: resolveMediaUrl(message.media_url), type: 'image' })}
                                  onLoad={(e) => { e.target.style.opacity = '1'; const skel = e.target.previousSibling; if (skel) skel.style.display = 'none'; }}
                                  onError={(e) => {
                                    const retryCount = parseInt(e.target.dataset.retryCount || '0');
                                    if (retryCount < 5) {
                                      e.target.dataset.retryCount = String(retryCount + 1);
                                      const delay = Math.min((retryCount + 1) * 1000, 3000);
                                      e.target.style.opacity = '0';
                                      setTimeout(() => { e.target.src = resolveMediaUrl(message.media_url) + (message.media_url.includes('?') ? '&' : '?') + 'v=' + Date.now(); }, delay);
                                    }
                                  }}
                                  loading="lazy"
                                />
                                <DownloadButton url={resolveMediaUrl(message.media_url)} filename={`image_${message.id}.jpg`} className="absolute bottom-2 right-2 z-20" />
                              </div>
                            ) : message.message_type === 'video' ? (
                              <div className="relative max-w-[240px]">
                                <video
                                  src={resolveMediaUrl(message.media_url)}
                                  className="w-full rounded-lg cursor-pointer"
                                  preload="metadata"
                                  playsInline
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const video = e.currentTarget;
                                    if (video.requestFullscreen) video.requestFullscreen();
                                    else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
                                    video.play();
                                  }}
                                  onLoadedMetadata={(e) => {
                                    const d = e.target.duration;
                                    if (d && isFinite(d)) {
                                      const el = e.target.parentElement?.querySelector('[data-video-duration]');
                                      if (el) el.textContent = `${Math.floor(d/60)}:${Math.floor(d%60).toString().padStart(2,'0')}`;
                                    }
                                  }}
                                  controls
                                />
                                <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/60 rounded-full px-2 py-0.5 pointer-events-none">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                                  <span data-video-duration className="text-[10px] text-white font-medium">0:00</span>
                                </div>
                                <DownloadButton url={resolveMediaUrl(message.media_url)} filename={`video_${message.id}.mp4`} className="absolute top-2 right-2 z-20" />
                              </div>
                            ) : message.message_type === 'voice' || message.message_type === 'audio' ? (
                              <AudioPlayer
                                src={message._localBlobUrl || resolveMediaUrl(message.media_url)}
                                messageId={message.id}
                                transcript={message.transcript}
                                isDarkMode={isDarkMode}
                                token={token}
                                isUploading={!!message._uploading}
                                onTranscribe={async (e) => {
                                  e?.stopPropagation();
                                  try {
                                    const r = await axios.post(`${API}/messages/${message.id}/transcribe`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                    if (r.data.transcript) {
                                      toast.success('Transcribed!');
                                      setMessages(prev => prev.map(m => m.id === message.id ? { ...m, transcript: r.data.transcript } : m));
                                    } else {
                                      toast.error('Could not transcribe');
                                    }
                                  } catch { toast.error('Transcription failed'); }
                                }}
                              />
                            ) : (
                              <button onClick={async () => { window.open(resolveMediaUrl(message.media_url), '_blank'); toast.success('Downloading...'); }} className="flex items-center gap-3 p-2 w-full text-left">
                                <FileText size={24} className="text-blue-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{message.media_url?.split('/').pop() || 'Document'}</p>
                                </div>
                                <ArrowLeft size={14} className={`flex-shrink-0 rotate-[-90deg] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                              </button>
                            )}
                          </div>
                        )}
                        {/* Text Content (hide for media-only messages) */}
                        {(!message.media_url || !['Photo', 'Video', 'Audio', 'File', 'Voice message'].includes(message.content?.replace(/^[\u{1F4F7}\u{1F3A5}\u{1F3B5}\u{1F4CE}\u{1F3A4}]\s*/u, ''))) && (
                          <div className={`relative ${message.media_url ? 'px-2 pb-1 pt-1' : ''}`}>
                            {isDeletedForEveryone ? (
                              <p className="text-sm leading-relaxed italic text-gray-400">
                                This message was deleted
                              </p>
                            ) : (
                              <div className="select-text cursor-text">
                                <ExpandableText content={getMessageContent(message)} isDarkMode={isDarkMode} formatFn={formatMessageText} />
                              </div>
                            )}
                            <div className={`flex items-center justify-end gap-1 text-[10px] mt-0.5 ${isSent ? 'text-[#8B7B4D]' : 'text-text-tertiary'}`}>
                              {message.encrypted && (
                                <Lock size={9} className={isSent ? 'text-[#8B7B4D]' : 'text-text-tertiary'} />
                              )}
                              <span className="whitespace-nowrap">{message.edited && <span className="italic mr-1">edited</span>}{formatMessageTime(message.timestamp)}</span>
                              {isSent && (
                                <MessageStatus message={message} recipientId={getRecipientId()} chatType={chat?.chat_type} participantCount={chat?.participants?.length} showBlueTicksEnabled={myReadReceiptsEnabled} />
                              )}
                            </div>
                          </div>
                        )}
                        {/* For media-only messages, show time separately */}
                        {message.media_url && ['Photo', 'Video', 'Audio', 'File', 'Voice message'].includes(message.content?.replace(/^[\u{1F4F7}\u{1F3A5}\u{1F3B5}\u{1F4CE}\u{1F3A4}]\s*/u, '')) && (
                          <div className={`flex items-center gap-1 mt-1 px-2 pb-1 text-xs ${isSent ? 'text-[#8B7B4D]' : 'text-text-tertiary'}`}>
                            {message.encrypted && (
                              <Lock size={10} className={isSent ? 'text-[#8B7B4D]' : 'text-text-tertiary'} />
                            )}
                            <span>{formatMessageTime(message.timestamp)}</span>
                            {isSent && (
                              <MessageStatus message={message} recipientId={getRecipientId()} chatType={chat?.chat_type} participantCount={chat?.participants?.length} showBlueTicksEnabled={myReadReceiptsEnabled} />
                            )}
                          </div>
                        )}
                      </div>
                      {/* Failed message retry button */}
                      {message._failed && (
                        <button
                          onClick={(e) => { e.stopPropagation(); retryMessage(message); }}
                          className="flex items-center gap-1 mt-1 px-2 py-1 rounded-full bg-red-500/10 hover:bg-red-500/20 transition-colors"
                          data-testid="retry-message-btn"
                        >
                          <AlertCircle size={12} className="text-red-500" />
                          <span className="text-[10px] text-red-500 font-medium">Failed - Tap to retry</span>
                        </button>
                      )}
                      {/* Reactions Display */}
                      {message.reactions && Object.keys(message.reactions).length > 0 && (
                        <div className={`flex flex-wrap gap-1 mt-1 ${isSent ? 'justify-end' : 'justify-start'}`}>
                          {Object.entries(message.reactions).map(([emoji, userIds]) => {
                            const hasReacted = userIds.includes(user.id);
                            return (
                              <button
                                key={emoji}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Long press or right-click shows details, single click toggles reaction
                                  handleReaction(message.id, emoji);
                                }}
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  fetchReactionsDetails(message.id);
                                }}
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  fetchReactionsDetails(message.id);
                                }}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-all ${
                                  hasReacted 
                                    ? 'bg-[#D4AF37]/20 border border-[#D4AF37]/40' 
                                    : isDarkMode 
                                      ? 'bg-gray-700 hover:bg-gray-600 border border-gray-600' 
                                      : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm'
                                }`}
                                title={`${userIds.length} reaction${userIds.length > 1 ? 's' : ''} - Double-click to see who`}
                              >
                                <span>{emoji}</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>{userIds.length}</span>
                              </button>
                            );
                          })}
                          {/* View all reactions button */}
                          <button
                            onClick={() => fetchReactionsDetails(message.id)}
                            className={`px-2 py-0.5 rounded-full text-xs transition-all ${
                              isDarkMode 
                                ? 'bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-400' 
                                : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm text-gray-500'
                            }`}
                            title="View all reactions"
                          >
                            •••
                          </button>
                        </div>
                      )}
                      {/* Replies count badge - shows when message has replies via Reply */}
                      {(message.thread_count > 0) && (() => {
                        const replies = messages.filter(m => m.reply_to === message.id || m.thread_parent_id === message.id);
                        const latestReply = replies.length > 0 ? replies[replies.length - 1] : null;
                        const preview = latestReply?.content ? latestReply.content.replace(/\n/g, ' ').slice(0, 30) : '';
                        return (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewRepliesFor({
                              id: message.id,
                              content: message.content,
                              senderName: isSent ? 'You' : (message.sender?.display_name || chat?.other_user?.display_name || 'User')
                            });
                          }}
                          className={`flex items-center gap-1.5 mt-1 px-2.5 py-1 rounded-lg text-xs transition-colors ${
                            isDarkMode 
                              ? 'text-[#D4AF37] hover:bg-gray-700/50' 
                              : 'text-[#B8960C] hover:bg-amber-50'
                          }`}
                          data-testid={`replies-badge-${message.id}`}
                        >
                          <Forward size={12} className="rotate-180 flex-shrink-0" />
                          <span className="font-medium whitespace-nowrap">{message.thread_count} {message.thread_count === 1 ? 'reply' : 'replies'}</span>
                          {preview && <span className={`truncate max-w-[150px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>· {preview}{latestReply.content.length > 30 ? '...' : ''}</span>}
                        </button>
                        );
                      })()}
                    </div>
                    )}
                  </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Media Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/90 flex flex-col z-50" data-testid="media-preview-modal">
          {/* Header */}
          <div className="p-4 flex items-center justify-between">
            <button
              onClick={cancelPreview}
              className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              data-testid="cancel-preview-btn"
            >
              <X size={24} className="text-white" />
            </button>
            <span className="text-white font-medium">{previewFile.name}</span>
            <div className="w-10" /> {/* Spacer */}
          </div>

          {/* Preview Area */}
          <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
            {previewUrl && previewFile.type.startsWith('image/') ? (
              <div className="relative">
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
                <button
                  onClick={() => setShowImageEditor(true)}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 text-white text-sm flex items-center gap-2 hover:bg-black/80 transition-colors"
                  data-testid="open-image-editor-btn"
                >
                  <Crop size={14} />
                  Edit
                </button>
              </div>
            ) : previewUrl && previewFile.type.startsWith('video/') ? (
              <video 
                src={previewUrl} 
                controls 
                className="max-w-full max-h-full rounded-lg"
              />
            ) : (
              <div className="text-center">
                <FileText size={80} className="text-white/50 mx-auto mb-4" />
                <p className="text-white text-lg">{previewFile.name}</p>
                <p className="text-white/60 text-sm mt-1">
                  {(previewFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            )}
          </div>

          {/* Footer with Caption and Send */}
          <div className="p-4 bg-black/50">
            <div className="chat-container max-w-2xl mx-auto">
              <div className="flex items-center gap-3">
                <textarea
                  value={previewCaption}
                  onChange={(e) => setPreviewCaption(e.target.value)}
                  onKeyDown={(e) => {
                    const isMobile = window.matchMedia('(pointer: coarse)').matches;
                    if (e.key === 'Enter' && !e.shiftKey && !isMobile && !uploading) {
                      e.preventDefault();
                      e.stopPropagation();
                      sendMediaMessage();
                    }
                  }}
                  autoFocus
                  rows={1}
                  placeholder="Add a caption..."
                  className="flex-1 bg-white/10 border-none rounded-xl px-4 py-3 text-white placeholder:text-white/50 focus:ring-1 focus:ring-[#D4AF37] transition-all resize-none overflow-y-auto text-sm"
                  style={{ minHeight: '48px', maxHeight: '100px' }}
                  data-testid="caption-input"
                />
                <button
                  onClick={sendMediaMessage}
                  disabled={uploading}
                  className="h-12 w-12 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white shadow-gold hover:scale-105 active:scale-95 transition-all duration-300 flex items-center justify-center disabled:opacity-50"
                  data-testid="send-media-btn"
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Progress Indicator */}
      {uploading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-6 w-64 text-center">
            <Loader2 size={40} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-text-primary font-medium mb-2">Uploading...</p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-sm text-text-secondary mt-2">{uploadProgress}%</p>
          </div>
        </div>
      )}

      {/* Image Editor */}
      {showImageEditor && previewUrl && (
        <ImageEditor
          imageUrl={previewUrl}
          onSave={(editedFile, editedUrl) => {
            setPreviewFile(editedFile);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(editedUrl);
            setShowImageEditor(false);
          }}
          onCancel={() => setShowImageEditor(false)}
        />
      )}

      {/* Schedule Message Modal */}
      {showScheduleModal && (
        <ScheduleMessageModal
          chatId={chatId}
          token={token}
          onClose={() => setShowScheduleModal(false)}
          onScheduled={() => toast.success('Message will be sent at the scheduled time')}
        />
      )}

      {/* Add Task FAB for Tasks Chat */}
      {isTasksChat && (
        <button
          onClick={() => setShowNewTaskModal(true)}
          className="fixed bottom-24 right-8 w-14 h-14 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white shadow-gold hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center z-20"
          data-testid="new-task-fab"
        >
          <Plus size={24} />
        </button>
      )}

      {/* Input Area */}
      <div className="sticky bottom-0 pb-3 pt-1 px-2" style={{ background: 'linear-gradient(transparent 0%, rgba(235,228,214,0.95) 20%)' }}>
        <div className="flex items-center gap-1 rounded-full bg-white px-2 py-1" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <form onSubmit={handleSendMessage} className="flex items-center gap-1 flex-1">
            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />

            {/* Plus Button with Action Menu */}
            <div className="relative">
              <button
                type="button"
                data-testid="plus-menu-btn"
                data-menu-trigger
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEmojiPicker(false);
                  setShowChatMenu(false);
                  setShowAttachMenu(!showAttachMenu);
                }}
                className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary-DEFAULT transition-colors"
              >
                <Plus size={22} className="text-text-primary" />
              </button>

              {/* Action Menu */}
              {showAttachMenu && (
                <div data-menu-dropdown className="absolute bottom-12 left-0 bg-white rounded-2xl shadow-lg border border-border-DEFAULT p-2 min-w-[220px] z-10">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAttachMenu(false);
                      const otherUser = chat?.chat_type === 'direct' ? chat?.participants?.find(id => id !== user?.id) : null;
                      if (chat?.chat_type === 'direct' && otherUser) {
                        setNewMessage('/task ');
                      } else {
                        setNewMessage('/task @');
                      }
                      document.querySelector('[data-testid="message-input"]')?.focus();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary-DEFAULT transition-colors"
                    data-testid="quick-create-task-btn"
                  >
                    <CheckSquare size={20} className="text-[#D4AF37]" />
                    <span className="text-text-primary whitespace-nowrap">Create Task</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAttachMenu(false);
                      setNewMessage('/task @I ');
                      document.querySelector('[data-testid="message-input"]')?.focus();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary-DEFAULT transition-colors"
                    data-testid="quick-personal-task-btn"
                  >
                    <User size={20} className="text-[#D4AF37]" />
                    <span className="text-text-primary whitespace-nowrap">Personal Task</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAttachMenu(false);
                      setNewMessage('/remind ');
                      document.querySelector('[data-testid="message-input"]')?.focus();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary-DEFAULT transition-colors"
                    data-testid="quick-schedule-meeting-btn"
                  >
                    <Bell size={20} className="text-purple-500" />
                    <span className="text-text-primary whitespace-nowrap">Reminder</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAttachMenu(false);
                      setShowScheduleModal(true);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary-DEFAULT transition-colors"
                    data-testid="schedule-message-btn"
                  >
                    <Clock size={20} className="text-[#D4AF37]" />
                    <span className="text-text-primary whitespace-nowrap">Schedule Message</span>
                  </button>
                  <div className="border-t border-border-DEFAULT my-1" />
                  <button
                    type="button"
                    onClick={() => {
                      fileInputRef.current.accept = 'image/*';
                      fileInputRef.current.click();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary-DEFAULT transition-colors"
                    data-testid="attach-image-btn"
                  >
                    <Image size={20} className="text-green-500" />
                    <span className="text-text-primary whitespace-nowrap">Photo</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      fileInputRef.current.accept = 'video/*';
                      fileInputRef.current.click();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary-DEFAULT transition-colors"
                    data-testid="attach-video-btn"
                  >
                    <Video size={20} className="text-red-500" />
                    <span className="text-text-primary whitespace-nowrap">Video</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      fileInputRef.current.accept = '.pdf,.doc,.docx';
                      fileInputRef.current.click();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary-DEFAULT transition-colors"
                    data-testid="attach-document-btn"
                  >
                    <FileText size={20} className="text-blue-500" />
                    <span className="text-text-primary whitespace-nowrap">Document</span>
                  </button>
                </div>
              )}
            </div>

            {/* Emoji Picker Button */}
            <div className="relative" ref={emojiPickerRef}>
              <button
                type="button"
                data-testid="emoji-btn"
                data-menu-trigger
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAttachMenu(false);
                  setShowChatMenu(false);
                  if (!showEmojiPicker) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setEmojiPickerPos({
                      bottom: window.innerHeight - rect.top + 8,
                      left: Math.max(8, rect.left)
                    });
                  }
                  setShowEmojiPicker(!showEmojiPicker);
                }}
                className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary-DEFAULT transition-colors"
              >
                <Smile size={20} className="text-text-primary" />
              </button>
              
              {/* Emoji Picker - via portal for overflow safety */}
              {showEmojiPicker && emojiPickerPos && createPortal(
                <div data-menu-dropdown data-emoji-picker className="fixed z-[9999]" style={{ bottom: emojiPickerPos.bottom, left: emojiPickerPos.left }}>
                  <Picker
                    data={data}
                    onEmojiSelect={handleEmojiSelect}
                    theme={isDarkMode ? 'dark' : 'light'}
                    previewPosition="none"
                    skinTonePosition="search"
                    maxFrequentRows={2}
                  />
                </div>,
                document.body
              )}
            </div>

            {/* Message Input with Mention Dropdown */}
            <div className="flex-1 relative">
              {/* Edit message banner */}
              {editingMessageId && (
                <div className={`flex items-center gap-2 px-3 py-2 mb-1 rounded-t-xl border-l-4 ${
                  isDarkMode ? 'bg-gray-800 border-[#D4AF37]' : 'bg-[#D4AF37]/10 border-[#D4AF37]'
                }`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#D4AF37]">Editing message</p>
                  </div>
                  <button 
                    onClick={() => {
                      setEditingMessageId(null);
                      setEditingContent('');
                      setNewMessage('');
                    }}
                    className="p-1 rounded-full hover:bg-gray-200"
                    data-testid="cancel-edit-btn"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              {/* Reply preview bar */}
              {replyToMessage && (
                <div className={`flex items-center gap-2 px-3 py-2 mb-1 rounded-t-xl border-l-4 ${
                  isDarkMode ? 'bg-gray-800 border-blue-400' : 'bg-blue-50 border-blue-400'
                }`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-blue-500">{replyToMessage.senderName}</p>
                    <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{replyToMessage.content}</p>
                  </div>
                  <button onClick={() => setReplyToMessage(null)} className="p-1 rounded-full hover:bg-gray-200"><X size={14} /></button>
                </div>
              )}
              <textarea
                ref={textareaRef}
                data-testid="message-input"
                value={newMessage}
                onChange={handleMessageChange}
                onKeyDown={(e) => {
                  // Desktop/webview: Enter sends, Shift+Enter = new line
                  // Mobile: Enter = new line (send via button only)
                  const isMobile = window.matchMedia('(pointer: coarse)').matches;
                  if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                onPaste={handlePaste}
                rows={1}
                className="w-full bg-transparent border-none px-1 py-1 text-[#1A1A1A] placeholder:text-[#999999] focus:ring-0 focus:outline-none resize-none overflow-y-auto text-sm"
                style={{ minHeight: '24px', maxHeight: '80px' }}
                placeholder={chat?.chat_type === 'group' ? "Type here" : "Type here"}
                style={{ lineHeight: '1.5' }}
              />

              {/* Task Suggestion Bubble */}
              {showTaskSuggestion && (
                <div className={`absolute bottom-14 left-0 right-0 p-3 rounded-xl shadow-lg border z-20 ${
                  isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-border-DEFAULT'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckSquare size={16} className="text-[#D4AF37]" />
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-text-secondary'}`}>
                        Create a task from this message?
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedMessage({ content: taskSuggestionMessage });
                          setShowTaskModal(true);
                          setShowTaskSuggestion(false);
                        }}
                        className="px-3 py-1 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white text-sm font-medium"
                        data-testid="create-task-suggestion-btn"
                      >
                        Create Task
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowTaskSuggestion(false)}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-text-tertiary hover:text-text-secondary'
                        }`}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Mention Dropdown */}
              {showMentionList && filteredMembers.length > 0 && (
                <div className="absolute bottom-14 left-0 right-0 bg-white rounded-xl shadow-lg border border-border-DEFAULT max-h-48 overflow-y-auto z-20">
                  {filteredMembers.map((member) => (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => insertMention(member)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary-DEFAULT transition-colors"
                      data-testid={`mention-${member.id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white text-sm font-medium">
                        {member.display_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="text-left">
                        <p className="text-text-primary font-medium">{member.display_name}</p>
                        <p className="text-xs text-text-secondary">@{member.username}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Voice Recording UI or Send Button */}
            {isRecording ? (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={cancelRecording}
                  className="h-10 w-10 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center hover:bg-red-500/30 transition-colors"
                  data-testid="cancel-recording-btn"
                >
                  <X size={20} />
                </button>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
                }`}>
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className={`text-sm font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatDuration(recordingDuration)}
                  </span>
                </div>
                <button type="button" onClick={stopRecording} className="h-10 w-10 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white shadow-gold flex items-center justify-center flex-shrink-0" data-testid="send-recording-btn">
                  <Send size={18} />
                </button>
              </div>
            ) : newMessage.trim() ? (
              <button type="submit" data-testid="send-btn" disabled={sending} className="h-8 w-8 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white flex items-center justify-center flex-shrink-0 disabled:opacity-50">
                <Send size={16} />
              </button>
            ) : (
              <button type="button" onClick={startRecording} data-testid="mic-btn" className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-gray-500">
                <Mic size={18} />
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Reminder Modal */}
      {showReminderModal && selectedMessage && (
        <SetReminderModal
          message={selectedMessage}
          chatId={chatId}
          onClose={() => {
            setShowReminderModal(false);
            setSelectedMessage(null);
          }}
          onSuccess={() => {
            // Refresh or show success
          }}
          isGroupChat={isGroupChat}
        />
      )}

      {/* Task Modal */}
      {showTaskModal && selectedMessage && (
        <CreateTaskModal
          message={selectedMessage}
          chatId={chatId}
          onClose={() => {
            setShowTaskModal(false);
            setSelectedMessage(null);
          }}
          onSuccess={() => {
            fetchMessages();
          }}
          chatParticipants={groupMembers}
        />
      )}

      {/* New Task Modal (for Tasks chat) */}
      {showNewTaskModal && (
        <CreateTaskModal
          message={null}
          chatId={chatId}
          onClose={() => setShowNewTaskModal(false)}
          onSuccess={() => {
            fetchMessages();
          }}
        />
      )}

      {/* Message Info Modal (for group read receipts) */}
      {showMessageInfo && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowMessageInfo(null)}
        >
          <div 
            className={`rounded-2xl p-6 w-full max-w-sm ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-heading text-lg ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>
                Message Info
              </h3>
              <button 
                onClick={() => setShowMessageInfo(null)}
                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
              >
                <X size={18} />
              </button>
            </div>
            
            {/* Read By */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCheck size={16} className="text-blue-500" />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>
                  Read by {showMessageInfo.read_count}/{showMessageInfo.total_recipients}
                </span>
              </div>
              {showMessageInfo.read_by?.length > 0 ? (
                <div className="space-y-2 ml-6">
                  {showMessageInfo.read_by.map(user => (
                    <div key={user.id} className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>
                      {user.display_name}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-sm ml-6 ${isDarkMode ? 'text-gray-500' : 'text-text-tertiary'}`}>
                  No one yet
                </div>
              )}
            </div>

            {/* Delivered To */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Check size={16} className="text-gray-400" />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>
                  Delivered to {showMessageInfo.delivered_count}/{showMessageInfo.total_recipients}
                </span>
              </div>
              {showMessageInfo.delivered_to?.filter(u => !showMessageInfo.read_by?.find(r => r.id === u.id)).length > 0 ? (
                <div className="space-y-2 ml-6">
                  {showMessageInfo.delivered_to
                    .filter(u => !showMessageInfo.read_by?.find(r => r.id === u.id))
                    .map(user => (
                      <div key={user.id} className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>
                        {user.display_name}
                      </div>
                    ))}
                </div>
              ) : (
                <div className={`text-sm ml-6 ${isDarkMode ? 'text-gray-500' : 'text-text-tertiary'}`}>
                  {showMessageInfo.read_by?.length === showMessageInfo.delivered_count ? 'All delivered messages have been read' : 'Pending delivery'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Group Info Sidebar */}
      {showGroupInfo && isGroupChat && (
        <GroupInfoSidebar
          chat={chat}
          groupMembers={groupMembers}
          isCurrentUserAdmin={isCurrentUserAdmin}
          isDarkMode={isDarkMode}
          user={user}
          token={token}
          groupMediaLoaded={groupMediaLoaded}
          groupSharedMedia={groupSharedMedia}
          onClose={() => setShowGroupInfo(false)}
          onAddMember={() => setShowAddMemberModal(true)}
          onMakeAdmin={makeAdmin}
          onRemoveAdmin={removeAdmin}
          onRemoveMember={removeMember}
          onLeaveGroup={() => { setShowGroupInfo(false); handleLeaveGroup(); }}
          onDeleteGroup={() => { setShowGroupInfo(false); handleDeleteGroup(); }}
          onSearchChat={() => { setShowGroupInfo(false); setShowInChatSearch(true); }}
          onViewContactProfile={(memberId) => { setShowGroupInfo(false); setViewingContactProfile({ userId: memberId, chatId }); }}
          onSetDisappearing={async (value, label) => {
            try {
              await axios.post(`${API}/chats/${chatId}/disappearing`, { duration: value }, { headers: { Authorization: `Bearer ${token}` } });
              toast.success(value === 0 ? 'Disappearing messages off' : `Messages disappear after ${label}`);
              setChat(prev => ({ ...prev, disappearing_messages: value }));
            } catch { toast.error('Failed to update'); }
          }}
          onMuteToggle={async () => {
            try {
              const isMuted = chat?.muted_by?.includes(user.id);
              if (isMuted) { await axios.delete(`${API}/chats/${chatId}/mute`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Unmuted'); }
              else { await axios.post(`${API}/chats/${chatId}/mute`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Muted'); }
            } catch { toast.error('Failed'); }
          }}
          onMediaClick={setLightboxMedia}
          API={API}
          axios={axios}
        />
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowAddMemberModal(false);
            setMemberSearch('');
            setMemberSearchResults([]);
          }}
        >
          <div 
            className={`w-full max-w-md rounded-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-xl overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
              <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Add Member</h3>
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setMemberSearch('');
                  setMemberSearchResults([]);
                }}
                className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <X size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
              </button>
            </div>
            
            {/* Search Input */}
            <div className="p-4">
              <input
                type="text"
                value={memberSearch}
                onChange={(e) => {
                  setMemberSearch(e.target.value);
                  searchForMembers(e.target.value);
                }}
                placeholder="Search by username or name..."
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' 
                    : 'bg-white border-gray-200 text-gray-900'
                }`}
                autoFocus
                data-testid="member-search-input"
              />
            </div>
            
            {/* Search Results */}
            <div className="max-h-60 overflow-y-auto px-4 pb-4">
              {searchingMembers ? (
                <div className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Searching...
                </div>
              ) : memberSearchResults.length === 0 && memberSearch ? (
                <div className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No users found
                </div>
              ) : (
                <div className="space-y-2">
                  {memberSearchResults.map(u => (
                    <div
                      key={u.id}
                      onClick={() => addMemberToGroup(u.id)}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer ${
                        isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">
                        {(u.display_name || u.username || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {u.display_name || u.username}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          @{u.username}
                        </p>
                      </div>
                      <UserPlus size={18} className="text-[#D4AF37]" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reactions Modal */}
      {showReactionsModal && reactionsData && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowReactionsModal(null);
            setReactionsData(null);
          }}
        >
          <div 
            className={`w-full max-w-sm rounded-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-xl overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
              <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Reactions</h3>
              <button
                onClick={() => {
                  setShowReactionsModal(null);
                  setReactionsData(null);
                }}
                className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <X size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
              </button>
            </div>
            
            {/* Reactions List */}
            <div className="max-h-80 overflow-y-auto">
              {reactionsData.reactions && Object.entries(reactionsData.reactions).map(([emoji, users]) => (
                <div key={emoji} className={`border-b last:border-0 ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                  {/* Emoji Header */}
                  <div className={`px-4 py-2 flex items-center gap-2 ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                    <span className="text-xl">{emoji}</span>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {users.length} {users.length === 1 ? 'person' : 'people'}
                    </span>
                  </div>
                  {/* Users who reacted */}
                  <div className="px-4 py-2 space-y-2">
                    {users.map((u) => (
                      <div key={u.id} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white text-sm font-medium">
                          {(u.display_name || u.username || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {u.display_name || u.username}
                            {u.id === user.id && (
                              <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(You)</span>
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Empty state */}
              {(!reactionsData.reactions || Object.keys(reactionsData.reactions).length === 0) && (
                <div className="p-8 text-center">
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No reactions yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Forward Message Modal */}
      {showForwardModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowForwardModal(null)}
        >
          <div 
            className={`w-full max-w-md rounded-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-xl overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
              <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Forward Message</h3>
              <button
                onClick={() => setShowForwardModal(null)}
                className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <X size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
              </button>
            </div>
            
            {/* Message Preview */}
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
              <p className={`text-sm line-clamp-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {showForwardModal.content}
              </p>
            </div>
            
            {/* Chat List */}
            <div className="max-h-80 overflow-y-auto">
              <p className={`px-4 py-2 text-xs font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                SELECT CHAT
              </p>
              {forwardChats.map(chat => {
                const displayName = chat.chat_type === 'group' 
                  ? chat.group_name 
                  : chat.other_user?.display_name || chat.other_user?.username || 'Chat';
                  
                return (
                  <button
                    key={chat.id}
                    onClick={() => forwardMessage(chat.id)}
                    className={`w-full flex items-center gap-3 p-4 transition-colors ${
                      isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-medium ${
                      chat.chat_type === 'group' 
                        ? 'bg-gradient-to-br from-[#D4AF37] to-[#C5A059]'
                        : 'bg-gradient-to-br from-[#D4AF37] to-[#C5A059]'
                    }`}>
                      {chat.chat_type === 'group' ? <Users size={20} /> : displayName?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {displayName}
                      </p>
                    </div>
                    <Forward size={16} className="text-[#D4AF37]" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Media Lightbox - rendered via portal */}
      {lightboxMedia && createPortal(
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9998]"
          onClick={() => setLightboxMedia(null)}
          data-testid="media-lightbox"
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 z-10"
            onClick={() => setLightboxMedia(null)}
          >
            <X size={24} className="text-white" />
          </button>
          {lightboxMedia.type === 'video' ? (
            <video src={lightboxMedia.url} controls autoPlay className="max-w-[90vw] max-h-[85vh] rounded-lg" onClick={(e) => e.stopPropagation()} />
          ) : (
            <img src={lightboxMedia.url} alt="" className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          )}
        </div>,
        document.body
      )}

      {/* Contact Profile Sidebar */}
      {viewingContactProfile && contactProfileData && (
        <div className="fixed inset-0 bg-black/50 flex justify-end z-50" onClick={() => setViewingContactProfile(null)}>
          <div 
            className={`w-full max-w-md h-full ${isDarkMode ? 'bg-gray-900' : 'bg-white'} overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-profile-sidebar"
          >
            {/* Sidebar Header */}
            <div className={`sticky top-0 glass-effect border-b ${isDarkMode ? 'border-gray-800' : 'border-border-DEFAULT'} p-4 flex items-center gap-3 z-10`}>
              <button 
                onClick={() => setViewingContactProfile(null)}
                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
                data-testid="close-contact-profile-btn"
              >
                <X size={20} className={isDarkMode ? 'text-white' : 'text-gray-900'} />
              </button>
              <h2 className={`font-heading text-lg ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>
                Contact Info
              </h2>
            </div>

            {/* Profile Section */}
            <div className={`p-6 text-center border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
              <div 
                className="w-28 h-28 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center mx-auto mb-4 text-white text-4xl font-medium cursor-pointer"
                onClick={() => {
                  if (contactProfileData.avatar_url) {
                    setExpandedAvatar(resolveAvatarUrl(contactProfileData.avatar_url));
                  }
                }}
                data-testid="contact-profile-avatar"
              >
                {contactProfileData.avatar_url ? (
                  <img src={resolveAvatarUrl(contactProfileData.avatar_url)} alt={contactProfileData.display_name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  (contactProfileData.display_name || contactProfileData.username || '?').charAt(0).toUpperCase()
                )}
              </div>
              <h3 className={`text-xl font-heading mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {contactProfileData.display_name || contactProfileData.username}
              </h3>
              <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                @{contactProfileData.username}
              </p>
              {contactProfileData.status_message && (
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {contactProfileData.status_message}
                </p>
              )}
              <div className="flex items-center justify-center gap-2 mt-3">
                <div className={`w-2 h-2 rounded-full ${contactProfileData.is_online ? 'bg-green-500' : 'bg-gray-400'}`} />
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {contactProfileData.is_online ? 'Online' : 'Offline'}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-center gap-4 mt-5">
                <button onClick={() => initiateCall('voice')} className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-colors ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`} data-testid="contact-voice-call-btn">
                  <Phone size={22} className="text-[#D4AF37]" />
                  <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Audio</span>
                </button>
                <button onClick={() => initiateCall('video')} className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-colors ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`} data-testid="contact-video-call-btn">
                  <Video size={22} className="text-[#D4AF37]" />
                  <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Video</span>
                </button>
                <button onClick={() => { setViewingContactProfile(null); setShowInChatSearch(true); }} className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-colors ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`} data-testid="contact-search-btn">
                  <Search size={22} className="text-[#D4AF37]" />
                  <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Search</span>
                </button>
              </div>
            </div>

            {/* About Section */}
            {contactProfileData.bio && (
              <div className={`p-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                <p className={`text-xs uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>About</p>
                <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>{contactProfileData.bio}</p>
              </div>
            )}

            {/* Media, Links, and Docs */}
            <div className={`border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="p-4">
                <p className={`text-xs uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Media, Links, and Docs
                </p>
                <div className="flex gap-2 mb-3">
                  {[
                    { id: 'media', label: 'Media', count: (contactSharedMedia.images?.length || 0) + (contactSharedMedia.videos?.length || 0) },
                    { id: 'docs', label: 'Docs', count: contactSharedMedia.documents?.length || 0 },
                    { id: 'links', label: 'Links', count: contactSharedMedia.links?.length || 0 }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setContactMediaTab(tab.id)}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                        contactMediaTab === tab.id
                          ? 'bg-[#D4AF37] text-white'
                          : isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {tab.label} ({tab.count})
                    </button>
                  ))}
                </div>
                {contactMediaTab === 'media' && (
                  <div className="grid grid-cols-3 gap-1">
                    {[...(contactSharedMedia.images || []), ...(contactSharedMedia.videos || [])].slice(0, 9).map((item, i) => (
                      <div key={i} className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-80" onClick={() => setLightboxMedia({ url: item.url, type: item.type === 'video' ? 'video' : 'image' })}>
                        {item.type === 'video' ? <video src={item.url} className="w-full h-full object-cover" /> : <img src={item.url} alt="" className="w-full h-full object-cover" />}
                      </div>
                    ))}
                    {(contactSharedMedia.images?.length || 0) + (contactSharedMedia.videos?.length || 0) === 0 && (
                      <p className={`col-span-3 text-center py-6 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No media shared yet</p>
                    )}
                  </div>
                )}
                {contactMediaTab === 'docs' && (
                  <div className="space-y-2">
                    {(contactSharedMedia.documents || []).slice(0, 5).map((doc, i) => (
                      <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-3 p-3 rounded-xl ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'}`}>
                        <FileText size={20} className="text-blue-500" />
                        <span className={`text-sm truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{doc.name || 'Document'}</span>
                      </a>
                    ))}
                    {(contactSharedMedia.documents?.length || 0) === 0 && (
                      <p className={`text-center py-6 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No documents shared yet</p>
                    )}
                  </div>
                )}
                {contactMediaTab === 'links' && (
                  <div className="space-y-2">
                    {(contactSharedMedia.links || []).slice(0, 5).map((link, i) => (
                      <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-3 p-3 rounded-xl ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'}`}>
                        <Image size={20} className="text-green-500" />
                        <span className={`text-sm truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{link.title || link.url}</span>
                      </a>
                    ))}
                    {(contactSharedMedia.links?.length || 0) === 0 && (
                      <p className={`text-center py-6 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No links shared yet</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Common Groups */}
            {contactCommonGroups.length > 0 && (
              <div className={`border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                <div className="p-4">
                  <p className={`text-xs uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {contactCommonGroups.length} Group{contactCommonGroups.length !== 1 ? 's' : ''} in Common
                  </p>
                  <div className="space-y-2">
                    {contactCommonGroups.map(group => (
                      <button key={group.id} onClick={() => { setViewingContactProfile(null); navigate(`/chat/${group.id}`); }} className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center"><Users size={18} className="text-white" /></div>
                        <div className="flex-1 text-left">
                          <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{group.group_name}</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{group.participant_count} members</p>
                        </div>
                        <ChevronRight size={16} className={isDarkMode ? 'text-gray-600' : 'text-gray-400'} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Actions Section */}
            <div className="p-4 space-y-1">
              <button onClick={async () => { try { if (contactIsMuted) { await axios.delete(`${API}/chats/${chatId}/mute`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Notifications unmuted'); } else { await axios.post(`${API}/chats/${chatId}/mute`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Notifications muted'); } setContactIsMuted(!contactIsMuted); } catch { toast.error('Failed to update mute'); } }} className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`} data-testid="contact-mute-btn">
                {contactIsMuted ? <BellOff size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} /> : <Bell size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />}
                <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{contactIsMuted ? 'Unmute Notifications' : 'Mute Notifications'}</span>
              </button>
              {/* Disappearing Messages */}
              <div className="px-4 py-3">
                <div className="flex items-center gap-4 mb-2">
                  <Clock size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                  <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>Disappearing messages</span>
                </div>
                <div className="flex gap-2 ml-9">
                  {[{ label: 'Off', value: 0 }, { label: '24h', value: 86400 }, { label: '7d', value: 604800 }, { label: '90d', value: 7776000 }].map(opt => (
                    <button key={opt.value} onClick={async () => { try { await axios.post(`${API}/chats/${chatId}/disappearing`, { duration: opt.value }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(opt.value === 0 ? 'Disappearing messages off' : `Messages disappear after ${opt.label}`); setChat(prev => ({ ...prev, disappearing_messages: opt.value })); } catch { toast.error('Failed to update'); } }} className={`px-3 py-1.5 rounded-full text-xs transition-colors ${(chat?.disappearing_messages || 0) === opt.value ? 'bg-[#D4AF37] text-white' : isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{opt.label}</button>
                  ))}
                </div>
              </div>
              <button onClick={async () => { const action = contactIsBlocked ? 'Unblock' : 'Block'; if (!window.confirm(`${action} this contact?`)) return; try { if (contactIsBlocked) { await axios.delete(`${API}/users/${viewingContactProfile.userId}/block`, { headers: { Authorization: `Bearer ${token}` } }); } else { await axios.post(`${API}/users/${viewingContactProfile.userId}/block`, {}, { headers: { Authorization: `Bearer ${token}` } }); } toast.success(`Contact ${action.toLowerCase()}ed`); setContactIsBlocked(!contactIsBlocked); } catch { toast.error(`Failed to ${action.toLowerCase()} contact`); } }} className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`} data-testid="contact-block-btn">
                <Ban size={20} className="text-red-500" />
                <span className="text-red-500">{contactIsBlocked ? 'Unblock Contact' : 'Block Contact'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Profile Pic Lightbox - rendered via portal to avoid stacking context issues */}
      {expandedAvatar && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center" onClick={() => setExpandedAvatar(null)} data-testid="avatar-lightbox">
          <button className="absolute top-6 right-6 text-white/80 hover:text-white z-10" onClick={(e) => { e.stopPropagation(); setExpandedAvatar(null); }}>
            <X size={28} />
          </button>
          <img src={expandedAvatar} alt="Profile" className="max-w-[90vw] max-h-[85vh] rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
        </div>,
        document.body
      )}
      {/* Replies Viewer Popup */}
      {viewRepliesFor && (() => {
        const parentMsg = messages.find(m => m.id === viewRepliesFor.id);
        const replies = messages.filter(m => m.reply_to === viewRepliesFor.id || m.thread_parent_id === viewRepliesFor.id);
        return createPortal(
          <div className="fixed inset-0 z-[9998] bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setViewRepliesFor(null)} data-testid="replies-popup-overlay">
            <div 
              className={`w-full sm:w-[420px] max-h-[70vh] rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'}`}
              onClick={(e) => e.stopPropagation()}
              data-testid="replies-popup"
            >
              {/* Header */}
              <div className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}</p>
                  <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>to: {viewRepliesFor.content?.slice(0, 50) || 'message'}{viewRepliesFor.content?.length > 50 ? '...' : ''}</p>
                </div>
                <button onClick={() => setViewRepliesFor(null)} className={`p-1.5 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} data-testid="replies-popup-close">
                  <X size={18} />
                </button>
              </div>

              {/* Parent message */}
              {parentMsg && (
                <div className={`px-4 py-2.5 border-b ${isDarkMode ? 'border-gray-800 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}>
                  <p className={`text-[10px] uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Original message</p>
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white text-[10px] font-medium flex-shrink-0 mt-0.5">
                      {viewRepliesFor.senderName?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs font-medium ${isDarkMode ? 'text-[#D4AF37]' : 'text-[#B8960C]'}`}>{viewRepliesFor.senderName}</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{parentMsg.content}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Replies list */}
              <div className="flex-1 overflow-y-auto px-4 py-2" data-testid="replies-list">
                {replies.length === 0 ? (
                  <p className={`text-sm text-center py-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No replies found in current view</p>
                ) : (
                  replies.map((reply, idx) => {
                    const sender = reply.sender?.display_name || (reply.sender_id === user.id ? 'You' : 'User');
                    const isMe = reply.sender_id === user.id;
                    return (
                      <button
                        key={reply.id}
                        onClick={() => { setViewRepliesFor(null); setTimeout(() => scrollToMessage(reply.id), 100); }}
                        className={`w-full text-left flex items-start gap-2.5 py-2.5 transition-colors rounded-lg px-2 -mx-2 ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'} ${idx < replies.length - 1 ? `border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}` : ''}`}
                        data-testid={`reply-item-${reply.id}`}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-medium flex-shrink-0 mt-0.5 ${isMe ? 'bg-gradient-to-br from-[#D4AF37] to-[#C5A059]' : 'bg-gradient-to-br from-gray-500 to-gray-600'}`}>
                          {sender.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            <span className={`text-xs font-medium ${isMe ? (isDarkMode ? 'text-[#D4AF37]' : 'text-[#B8960C]') : (isDarkMode ? 'text-gray-300' : 'text-gray-700')}`}>{sender}</span>
                            <span className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                              {reply.timestamp ? new Date(reply.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </div>
                          <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{reply.content}</p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

export default ChatInterface;
